<template>
  <div class="about-container">
    <section class="hero-section">
      <div class="hero-overlay"></div>
      <div class="hero-content">
        <div class="profile-circle">
          <img src="@/assets/EMS.png" alt="Enrico Michael Severe" class="profile-img" />
        </div>
        <h1 class="hero-title">Enrico Michael Severe</h1>
        <p class="hero-subtitle">
          Private Researcher · Author · Founder · Economic Philosopher
        </p>
        <div class="hero-lineage">
          <span class="lineage-rule"></span>
          <span class="lineage-text">
            <span class="lineage-crown">♛</span>
            Davidic Heir · Son of King David
            <span class="lineage-crown">♛</span>
          </span>
          <span class="lineage-rule"></span>
        </div>
        <p class="hero-description">
          Exploring the intersections of corporate power, systemic structures,
          and societal transformation through research, writing, and community action —
          grounded in the conviction that Yahweh's design for human flourishing
          is the foundation of every just economy.
        </p>
        <div class="hero-stats">
          <div class="stat-item">
            <span class="stat-number">{{ totalBookCount }}</span>
            <span class="stat-label">Published Books</span>
          </div>
          <div class="stat-divider"></div>
          <div class="stat-item">
            <span class="stat-number">{{ recentPosts.length }}+</span>
            <span class="stat-label">Research Articles</span>
          </div>
          <div class="stat-divider"></div>
          <div class="stat-item">
            <span class="stat-number">{{ quotes.length }}</span>
            <span class="stat-label">Guiding Principles</span>
          </div>
        </div>
      </div>
      <div class="scroll-indicator">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <polyline points="6 9 12 15 18 9"></polyline>
        </svg>
      </div>
    </section>

    <section class="lineage-section">
      <div class="lineage-inner">
        <div class="lineage-left">
          <div class="lin-eyebrow">Royal Lineage &amp; Scriptural Identity</div>
          <h2 class="lin-title">Davidic Heir,<br />Son of King David</h2>
          <p class="lin-body">
            Scripture is not merely an intellectual framework for my research — it is the
            foundation of my identity. I am a Davidic heir, a descendant in the line of
            King David, and that lineage shapes how I understand stewardship, justice,
            and the call to build institutions that reflect the character of Yahweh.
          </p>
          <p class="lin-body lin-body--secondary">
            The same King David who wrote that the earth is the LORD's and the fullness
            thereof (Psalm 24:1) understood that earthly power is held in trust — not
            owned. That conviction runs through everything I write, found, and build.
          </p>
          <p class="lin-body lin-body--secondary">
            Benevolism is not merely an economic philosophy — it is a covenant framework.
            Civil Neighbr is not merely a platform — it is a modern expression of the
            command to love your neighbor. And every book I publish is an act of bearing
            witness to the systems that violate that covenant.
          </p>
          <div class="lin-badges">
            <div class="lin-badge lin-badge--gold">
              <span class="lin-badge-crown">♛</span>
              Davidic Heir
            </div>
            <div class="lin-badge lin-badge--indigo">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"></path>
                <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"></path>
              </svg>
              Scripture Researcher
            </div>
            <a href="/scripture" class="lin-badge lin-badge--link">
              Explore My Scripture Research →
            </a>
          </div>
        </div>

        <div class="lineage-right">
          <div class="psalm-card">
            <div class="psalm-card__rule"></div>
            <div class="psalm-card__crown">♛</div>
            <blockquote class="psalm-card__quote">
              "The earth is the LORD's, and the fulness thereof; the world,
              and they that dwell therein."
            </blockquote>
            <div class="psalm-card__ref">— Psalm 24:1 (KJV)</div>
            <div class="psalm-card__rule"></div>
          </div>

          <div class="lin-pillars">
            <div class="lin-pillar" v-for="(p, i) in identityPillars" :key="i">
              <div class="lin-pillar__icon">{{ p.icon }}</div>
              <div>
                <div class="lin-pillar__title">{{ p.title }}</div>
                <div class="lin-pillar__text">{{ p.text }}</div>
              </div>
            </div>
          </div>

          <a href="https://thedavidicheir.com" target="_blank" rel="noopener" class="davidic-heir-card">
            <div class="davidic-heir-card__crown">♛</div>
            <div class="davidic-heir-card__body">
              <div class="davidic-heir-card__eyebrow">The Full Scriptural Case</div>
              <div class="davidic-heir-card__title">The Davidic Heir</div>
              <p class="davidic-heir-card__text">
                Two messiahs. The Isaiah 9:6 mistranslation. The Ezekiel 46:18 headlock.
                The Dabela of Enoch. The argument that cannot be denied with Scripture.
              </p>
              <div class="davidic-heir-card__link">
                thedavidicheir.com
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                  <polyline points="12 5 19 12 12 19"></polyline>
                </svg>
              </div>
            </div>
          </a>

          <div class="lin-scripture-cta">
            <div class="lin-scripture-cta__tag">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"></path>
                <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"></path>
              </svg>
              Scripture · Higher Dimensions · Enoch · KJV
            </div>
            <p class="lin-scripture-cta__text">
              Explore my ongoing research on Scripture as a book of physics —
              from Ezekiel's wheels to Paul's fourth dimension — and why Yahweh
              is the only rational conclusion the cosmos allows.
            </p>
            <a href="/scripture" class="lin-scripture-cta__btn">
              <span>Read Scripture Research</span>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                <line x1="5" y1="12" x2="19" y2="12"></line>
                <polyline points="12 5 19 12 12 19"></polyline>
              </svg>
            </a>
          </div>
        </div>
      </div>
    </section>

    <section class="benevolism-section">
      <div class="benv-bg-grid"></div>
      <div class="benv-glow"></div>
      <div class="benv-inner">
        <div class="benv-left">
          <div class="benv-eyebrow">New School of Economic Thought</div>
          <div class="benv-logo-row">
            <div class="benv-mark">B</div>
            <h2 class="benv-name">BENEVOLISM</h2>
          </div>
          <h3 class="benv-heading">
            Economies grow when businesses <em>take less.</em>
          </h3>
          <p class="benv-body">
            Benevolism is the economic philosophy I founded — built on a simple but transformative
            belief: prosperity grows when businesses take less from the people they serve. It aligns
            profit with participation, proving that structural fairness is not charity, but the most
            powerful engine of shared prosperity ever conceived.
          </p>
          <p class="benv-body benv-body--secondary">
            Drawing from my background in accounting and finance, I recognized a pattern that
            traditional economics often overlooks: extraction-based business models quietly drain
            communities, suppress disposable income, and limit participation in the economy.
          </p>
          <div class="benv-convictions">
            <div class="benv-conviction" v-for="(c, i) in benevolismConvictions" :key="i">
              <div class="benv-conviction-num">{{ String(i + 1).padStart(2, '0') }}</div>
              <div>
                <div class="benv-conviction-title">{{ c.title }}</div>
                <div class="benv-conviction-text">{{ c.text }}</div>
              </div>
            </div>
          </div>
          <div class="benv-founder-badge">
            <div class="benv-badge-dot"></div>
            <span>I am the Founder &amp; Architect of Benevolism</span>
          </div>
        </div>

        <div class="benv-right">
          <div class="benv-book-card">
            <div class="benv-book-crown">♛</div>
            <div class="benv-book-title-text">THE<br><strong>BENEVOLENT<br>ECONOMY</strong></div>
            <div class="benv-book-sub">How Wealth Grows<br>When Businesses Take Less</div>
            <div class="benv-book-divider"></div>
            <div class="benv-book-star">★</div>
            <div class="benv-book-treatise">A Treatise on Benevolism</div>
            <div class="benv-book-scales">⚖</div>
            <div class="benv-book-author">ENRICO MICHAEL SEVERE</div>
            <div class="benv-book-heir">Davidic Heir, Son of King David</div>
            <div class="benv-book-badge">
              <div class="benv-badge-inner">
                <div class="benv-badge-top">FOUNDING</div>
                <div class="benv-badge-main">TREATISE</div>
                <div class="benv-badge-bottom">VOL. I</div>
              </div>
            </div>
          </div>

          <div class="benv-laws-preview">
            <div class="benv-laws-header">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M12 2L2 7l10 5 10-5-10-5z"></path>
                <path d="M2 17l10 5 10-5M2 12l10 5 10-5"></path>
              </svg>
              The Ten Laws of the Benevolent Economy
            </div>
            <div class="benv-laws-list">
              <div class="benv-law-item" v-for="(law, i) in tenLawsPreview" :key="i">
                <span class="benv-law-num">{{ law.num }}</span>
                <span class="benv-law-name">{{ law.name }}</span>
              </div>
            </div>
          </div>

          <div class="benv-cta-card">
            <div class="benv-cta-tag">Now Live · benevolism.com</div>
            <p class="benv-cta-text">
              Read the founding treatise, explore the ten laws, and join the movement
              building a more benevolent economy.
            </p>
            <a href="https://benevolism.com" target="_blank" rel="noopener" class="benv-cta-btn">
              <span>Explore Benevolism</span>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                <line x1="5" y1="12" x2="19" y2="12"></line>
                <polyline points="12 5 19 12 12 19"></polyline>
              </svg>
            </a>
            <a href="https://www.amazon.com/dp/B0H5G63GSY" target="_blank" rel="noopener" class="benv-secondary-link">
              Get "The Benevolent Economy" on Amazon →
            </a>
          </div>
        </div>
      </div>
    </section>

    <section class="nextup-section">
      <div class="nextup-bg-grid"></div>
      <div class="nextup-glow"></div>
      <div class="nextup-inner">
        <div class="nextup-left">
          <div class="nextup-eyebrow">Algorithmic Research Operation</div>
          <div class="nextup-logo-row">
            <div class="nextup-mark">NW</div>
            <div class="nextup-name-block">
              <h2 class="nextup-name">NextUp</h2>
              <span class="nextup-name-sub">Wealth Management</span>
            </div>
          </div>
          <div class="nextup-algo-tag">
            BENEVOLISM<span class="nextup-accent">::</span>ALGO — ENGINE v4.2.1
          </div>
          <h3 class="nextup-heading">
            The machine extracts.<br><em>We redirect.</em>
          </h3>
          <p class="nextup-body">
            NextUp Wealth Management is a private algorithmic trading research firm — built
            to operate at machine speed and without emotion. We run the current financial
            system's own instruments, dispassionately and precisely, and aim every dollar
            of surplus at something larger than accumulation.
          </p>
          <p class="nextup-body nextup-body--secondary">
            The financial system was not designed to fail. It was designed to extract.
            NextUp exists at the intersection of that reality and Benevolism's response to it —
            mastering the tools of the current order to fund the architecture of a better one.
          </p>
          <div class="nextup-tenets">
            <div class="nextup-tenet" v-for="(t, i) in nextupTenets" :key="i">
              <div class="nextup-tenet-glyph">{{ t.glyph }}</div>
              <div>
                <div class="nextup-tenet-title">{{ t.title }}</div>
                <div class="nextup-tenet-text">{{ t.text }}</div>
              </div>
            </div>
          </div>
          <div class="nextup-founder-badge">
            <div class="nextup-badge-dot"></div>
            <span>I am the Founder of NextUp Wealth Management</span>
          </div>
        </div>

        <div class="nextup-right">
          <div class="nextup-terminal-card">
            <div class="nextup-terminal-bar">
              <span class="nt-dot nt-red"></span>
              <span class="nt-dot nt-amber"></span>
              <span class="nt-dot nt-green"></span>
              <span class="nextup-terminal-title">benevolism_engine.py</span>
              <span class="nt-live">
                <span class="nt-live-dot"></span>
                LIVE
              </span>
            </div>
            <div class="nextup-terminal-body">
              <div class="nt-line" v-for="(line, i) in nextupTerminalLines" :key="i" :class="line.type">
                <span class="nt-prefix">{{ line.prefix }}</span>
                <span class="nt-text">{{ line.text }}</span>
              </div>
              <div class="nt-cursor">█</div>
            </div>
          </div>

          <div class="nextup-ledger">
            <div class="nextup-ledger-header">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <line x1="12" y1="2" x2="12" y2="22"></line>
                <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
              </svg>
              The Cost of the Current System
            </div>
            <div class="nextup-ledger-grid">
              <div class="nextup-ledger-item" v-for="item in nextupLedger" :key="item.label">
                <div class="nextup-ledger-fig" :style="{ color: item.color }">{{ item.figure }}</div>
                <div class="nextup-ledger-label">{{ item.label }}</div>
                <div class="nextup-ledger-note">{{ item.note }}</div>
              </div>
            </div>
          </div>

          <div class="nextup-cta-card">
            <div class="nextup-cta-tag">Private · Not Open to Investors</div>
            <p class="nextup-cta-text">
              This is proprietary research. The returns fund a philosophy, not a fund.
              The work speaks when it is ready.
            </p>
            <a href="https://nextupwealthmanagement.com" target="_blank" rel="noopener" class="nextup-cta-btn">
              <span>Visit NextUp Wealth Management</span>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                <line x1="5" y1="12" x2="19" y2="12"></line>
                <polyline points="12 5 19 12 12 19"></polyline>
              </svg>
            </a>
          </div>
        </div>
      </div>
    </section>

    <section class="civil-neighbr-section">
      <div class="cn-bg-shapes">
        <div class="cn-shape cn-shape-1"></div>
        <div class="cn-shape cn-shape-2"></div>
        <div class="cn-grain"></div>
      </div>
      <div class="cn-inner">
        <div class="cn-left">
          <div class="cn-eyebrow">Now Launching</div>
          <div class="cn-logo-row">
            <div class="cn-logo-mark">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#fbeee6" stroke-width="2">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                <circle cx="9" cy="7" r="4"></circle>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
              </svg>
            </div>
            <h2 class="cn-platform-name">Civil Neighbr</h2>
          </div>
          <h3 class="cn-heading">From research to real-world change</h3>
          <p class="cn-body">
            After years of examining the systems that fail ordinary people — from corporate exploitation
            to the prison industrial complex — I'm helping build something different. Civil Neighbr is
            a community platform connecting neighbors with free food, clothing, and local support in real time.
          </p>
          <p class="cn-body cn-body--secondary">
            No red tape. No bureaucracy. Just neighbors helping neighbors — the kind of direct,
            community-powered action my research has always pointed toward.
          </p>
          <div class="cn-pillars">
            <div class="cn-pillar">
              <div class="cn-pillar-icon">🤝</div>
              <div>
                <div class="cn-pillar-title">Real-time giving</div>
                <div class="cn-pillar-sub">Donations posted the moment they're available</div>
              </div>
            </div>
            <div class="cn-pillar">
              <div class="cn-pillar-icon">📍</div>
              <div>
                <div class="cn-pillar-title">Hyper-local</div>
                <div class="cn-pillar-sub">Matched to your neighborhood, not a zip code away</div>
              </div>
            </div>
            <div class="cn-pillar">
              <div class="cn-pillar-icon">🔓</div>
              <div>
                <div class="cn-pillar-title">100% free</div>
                <div class="cn-pillar-sub">No paywalls, no ads — community-first always</div>
              </div>
            </div>
          </div>
          <div class="cn-founder-badge">
            <div class="cn-badge-dot"></div>
            <span>I'm a Founder of Civil Neighbr</span>
          </div>
        </div>

        <div class="cn-right">
          <div class="cn-quote-card">
            <div class="cn-quote-mark">"</div>
            <p class="cn-quote-text">
              My work examines the complex systems of power that shape modern society.
              Civil Neighbr is the other side of that coin — not just naming the problem,
              but building something to fix it.
            </p>
            <div class="cn-quote-attr">— Enrico Michael Severe, Founder</div>
          </div>
          <div class="cn-stats-grid">
            <div class="cn-stat">
              <div class="cn-stat-num">Local</div>
              <div class="cn-stat-lbl">Community first</div>
            </div>
            <div class="cn-stat">
              <div class="cn-stat-num">Real-time</div>
              <div class="cn-stat-lbl">Donation alerts</div>
            </div>
            <div class="cn-stat">
              <div class="cn-stat-num">Free</div>
              <div class="cn-stat-lbl">Always &amp; forever</div>
            </div>
          </div>
          <div class="cn-beta-card">
            <div class="cn-beta-tag">Private Beta · Now Open</div>
            <p class="cn-beta-text">
              We're opening doors in waves. Join the waitlist and receive your founding
              member badge when your wave opens.
            </p>
            <a href="https://civilneighbr.com" target="_blank" rel="noopener" class="cn-cta-btn">
              <span>Join Civil Neighbr</span>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                <line x1="5" y1="12" x2="19" y2="12"></line>
                <polyline points="12 5 19 12 12 19"></polyline>
              </svg>
            </a>
            <a href="https://civilneighbr.com/login" target="_blank" rel="noopener" class="cn-secondary-link">
              Already have an account? Sign in →
            </a>
          </div>
        </div>
      </div>
    </section>

    <section class="code-forge-section">
      <div class="cf-bg-grid"></div>
      <div class="cf-glow"></div>
      <div class="cf-inner">
        <div class="cf-left">
          <div class="cf-eyebrow">Est. 2024 — A Holding Company</div>
          <div class="cf-logo-row">
            <div class="cf-mark">CF</div>
            <div class="cf-name-block">
              <h2 class="cf-name">Code Forge</h2>
              <span class="cf-name-sub">Holdings</span>
            </div>
          </div>
          <h3 class="cf-heading">
            Software doesn't have<br><em>to be extractive.</em>
          </h3>
          <p class="cf-body">
            Code Forge Holdings builds and invests in software across categories — not just one
            kind of product. We think software doesn't have to be extractive to be sustainable:
            it can be priced fairly, built well, and still work as a business.
          </p>
          <p class="cf-body cf-body--secondary">
            Every company we launch or back shares that same discipline underneath — honest
            pricing, real engineering standards, and founders who stay hands-on long after launch.
          </p>
          <div class="cf-principles">
            <div class="cf-principle" v-for="(p, i) in cfPrinciples" :key="i">
              <div class="cf-principle-num">{{ String(i + 1).padStart(2, '0') }}</div>
              <div>
                <div class="cf-principle-title">{{ p.title }}</div>
                <div class="cf-principle-text">{{ p.text }}</div>
              </div>
            </div>
          </div>
          <div class="cf-founder-badge">
            <div class="cf-badge-dot"></div>
            <span>I am the Founder &amp; President of Code Forge Holdings</span>
          </div>
        </div>

        <div class="cf-right">
          <div class="cf-stats-card">
            <div class="cf-stats-grid">
              <div class="cf-stat-item" v-for="s in cfStats" :key="s.label">
                <div class="cf-stat-num">{{ s.num }}</div>
                <div class="cf-stat-label">{{ s.label }}</div>
              </div>
            </div>
          </div>

          <div class="cf-portfolio-card">
            <div class="cf-portfolio-header">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <rect x="3" y="3" width="7" height="7"></rect>
                <rect x="14" y="3" width="7" height="7"></rect>
                <rect x="14" y="14" width="7" height="7"></rect>
                <rect x="3" y="14" width="7" height="7"></rect>
              </svg>
              Some of Our Products
            </div>
            <a
              v-for="item in cfPortfolio"
              :key="item.name"
              :href="item.link"
              target="_blank"
              rel="noopener"
              class="cf-portfolio-item"
            >
              <div>
                <div class="cf-portfolio-name">{{ item.name }}</div>
                <div class="cf-portfolio-category">{{ item.category }}</div>
              </div>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                <line x1="7" y1="17" x2="17" y2="7"></line>
                <polyline points="7 7 17 7 17 17"></polyline>
              </svg>
            </a>
          </div>

          <div class="cf-cta-card">
            <div class="cf-cta-tag">Independently Operated</div>
            <p class="cf-cta-text">
              A look at some of the independent products — and one nonprofit — we build and
              invest in. See the full portfolio at codeforgedevelopment.com.
            </p>
            <a href="https://codeforgedevelopment.com" target="_blank" rel="noopener" class="cf-cta-btn">
              <span>Visit Code Forge Holdings</span>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                <line x1="5" y1="12" x2="19" y2="12"></line>
                <polyline points="12 5 19 12 12 19"></polyline>
              </svg>
            </a>
          </div>
        </div>
      </div>
    </section>

    <section class="philosophy-section">
      <div class="section-header">
        <span class="section-label">Philosophy</span>
        <h2 class="section-title">Guiding Principles</h2>
        <p class="section-description">Wisdom that shapes my research and perspective on the world</p>
      </div>
      <div v-if="quotesLoading" class="loading-container">
        <div class="spinner"></div>
        <p>Loading quotes...</p>
      </div>
      <div v-else-if="quotes.length === 0" class="empty-quotes-state">
        <p>No quotes available yet</p>
      </div>
      <div v-else class="quotes-carousel">
        <transition-group name="fade" tag="div" class="quote-container">
          <div
            v-for="(quote, index) in quotes"
            :key="index"
            v-show="activeQuoteIndex === index"
            class="quote-card-large"
          >
            <div class="quote-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="currentColor">
                <path d="M6 17h3l2-4V7H5v6h3zm8 0h3l2-4V7h-6v6h3z"/>
              </svg>
            </div>
            <p class="quote-text">{{ quote.text }}</p>
            <p class="quote-author">— {{ quote.author }}</p>
          </div>
        </transition-group>
        <div class="carousel-controls">
          <button
            v-for="(quote, index) in quotes"
            :key="'dot-' + index"
            @click="activeQuoteIndex = index"
            :class="['carousel-dot', { active: activeQuoteIndex === index }]"
          ></button>
        </div>
      </div>
    </section>

    <section class="books-section">
      <div class="section-header">
        <span class="section-label">Publications</span>
        <h2 class="section-title">Published Works</h2>
        <p class="section-description">Deep dives into corporate power structures and their societal impacts</p>
      </div>
      <div class="series-block">
        <div class="series-block-header">
          <div class="series-block-left">
            <div class="series-mark">B</div>
            <div>
              <div class="series-eyebrow">Ongoing Series</div>
              <h3 class="series-title">The Benevolism Series</h3>
            </div>
          </div>
          <div class="series-vol-count">
            <span class="series-vol-num">{{ benevolismSeries.length }}</span>
            <span class="series-vol-label">Volumes Published</span>
          </div>
        </div>
        <div class="series-grid">
          <div v-for="(vol, index) in benevolismSeries" :key="vol.title" class="series-vol-card">
            <div class="series-cover-wrap">
              <div class="series-cover" :class="vol.theme">
                <div class="sc-crown">♛</div>
                <div class="sc-title">{{ vol.titleLine1 }}<br><strong>{{ vol.titleLine2 }}</strong></div>
                <div class="sc-divider"></div>
                <div class="sc-scales">⚖</div>
                <div class="sc-divider"></div>
                <div class="sc-series">The Benevolism Series — Volume {{ vol.volume }}</div>
                <div class="sc-author">ENRICO MICHAEL SEVERE</div>
                <div class="sc-heir">Davidic Heir, Son of King David</div>
              </div>
              <div class="series-vol-badge">VOL. {{ vol.volume }}</div>
            </div>
            <div class="series-vol-info">
              <div class="series-vol-tag">Volume {{ vol.volume }}</div>
              <h4 class="series-vol-title">{{ vol.title }}</h4>
              <p class="series-vol-sub">{{ vol.subtitle }}</p>
              <p class="series-vol-desc">{{ vol.description }}</p>
              <a v-if="!vol.comingSoon" :href="vol.link" target="_blank" rel="noopener" class="series-vol-btn">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                  <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                  <polyline points="15 3 21 3 21 9"></polyline>
                  <line x1="10" y1="14" x2="21" y2="3"></line>
                </svg>
                Buy on Amazon
              </a>
              <span v-else class="series-vol-btn series-vol-btn--soon">Coming Soon</span>
            </div>
          </div>
        </div>
        <div class="series-footer">
          <div class="series-footer-left">
            <span class="series-footer-label">Fourteen volumes published across healthcare, finance, education, technology, public policy, housing, transportation, energy, food, labor, retail, entertainment &amp; real estate — with more forthcoming.</span>
          </div>
          <a href="https://benevolism.com" target="_blank" rel="noopener" class="series-footer-link">
            Explore the full series at benevolism.com →
          </a>
        </div>
      </div>
      <div class="series-block covenant-series-block">
        <div class="series-block-header">
          <div class="series-block-left">
            <div class="series-mark series-mark--covenant">C</div>
            <div>
              <div class="series-eyebrow">A Global Application</div>
              <h3 class="series-title">The Covenant Series</h3>
            </div>
          </div>
          <div class="series-vol-count">
            <span class="series-vol-num">{{ covenantSeries.length }}</span>
            <span class="series-vol-label">Volumes Published</span>
          </div>
        </div>
        <div class="series-grid">
          <div v-for="(vol, index) in covenantSeries" :key="vol.title" class="series-vol-card">
            <div class="series-cover-wrap">
              <img :src="vol.cover" :alt="vol.title" class="series-cover-img" />
            </div>
            <div class="series-vol-info">
              <div class="series-vol-tag">{{ vol.region }}</div>
              <h4 class="series-vol-title">{{ vol.title }}</h4>
              <p class="series-vol-sub">{{ vol.subtitle }}</p>
              <p class="series-vol-desc">{{ vol.description }}</p>
              <a :href="vol.link" target="_blank" rel="noopener" class="series-vol-btn">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                  <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                  <polyline points="15 3 21 3 21 9"></polyline>
                  <line x1="10" y1="14" x2="21" y2="3"></line>
                </svg>
                Buy on Amazon
              </a>
            </div>
          </div>
        </div>
        <div class="series-footer">
          <div class="series-footer-left">
            <span class="series-footer-label">Where the Benevolism Series builds the domestic doctrine, the Covenant Series takes it to international scope.</span>
          </div>
          <a href="https://benevolism.com" target="_blank" rel="noopener" class="series-footer-link">
            Explore Benevolism at benevolism.com →
          </a>
        </div>
      </div>
      <div class="other-works-header">
        <div class="other-works-rule"></div>
        <span class="other-works-label">Also Published</span>
        <div class="other-works-rule"></div>
      </div>
      <div class="books-grid">
        <div
          v-for="(book, index) in books"
          :key="book.title"
          class="book-card-modern"
          :style="{ animationDelay: `${index * 0.1}s` }"
        >
          <div class="book-cover-wrapper">
            <img :src="book.cover" :alt="book.title" class="book-cover-img" />
            <div class="book-overlay">
              <a :href="book.link" target="_blank" class="view-book-btn">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                  <polyline points="15 3 21 3 21 9"></polyline>
                  <line x1="10" y1="14" x2="21" y2="3"></line>
                </svg>
                View on Amazon
              </a>
            </div>
          </div>
          <div class="book-info">
            <h3 class="book-title-modern">{{ book.title }}</h3>
            <p class="book-author-modern">{{ book.author }}</p>
            <p v-if="book.description" class="book-description">{{ book.description }}</p>
          </div>
        </div>
      </div>
    </section>

    <section class="blog-preview-section">
      <div class="section-header">
        <span class="section-label">Latest Thoughts</span>
        <h2 class="section-title">Recent Research &amp; Reflections</h2>
        <p class="section-description">Exploring contemporary issues through critical analysis</p>
      </div>
      <div v-if="postsLoading" class="loading-container">
        <div class="spinner"></div>
        <p>Loading recent posts...</p>
      </div>
      <div v-else-if="recentPosts.length > 0" class="blog-grid">
        <div
          v-for="(post, index) in recentPosts"
          :key="post.id"
          class="blog-preview-card"
          :style="{ animationDelay: `${index * 0.15}s` }"
        >
          <div v-if="post.imageUrl" class="blog-preview-image">
            <img :src="post.imageUrl" :alt="post.title" />
            <div class="blog-category-badge" v-if="post.category">{{ post.category }}</div>
          </div>
          <div class="blog-preview-content">
            <div class="blog-meta">
              <span class="blog-date">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <circle cx="12" cy="12" r="10"></circle>
                  <polyline points="12 6 12 12 16 14"></polyline>
                </svg>
                {{ formatDate(post.createdAt) }}
              </span>
              <span class="blog-reading-time">{{ estimateReadingTime(post.content) }} min read</span>
            </div>
            <h3 class="blog-preview-title">{{ post.title }}</h3>
            <p class="blog-preview-excerpt">{{ post.excerpt || truncate(post.content, 120) }}</p>
            <div class="blog-tags" v-if="post.tags && post.tags.length > 0">
              <span v-for="tag in post.tags.slice(0, 3)" :key="tag" class="blog-tag">{{ tag }}</span>
            </div>
            <router-link to="/blog" class="read-more-link">
              Read Article
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <line x1="5" y1="12" x2="19" y2="12"></line>
                <polyline points="12 5 19 12 12 19"></polyline>
              </svg>
            </router-link>
          </div>
        </div>
      </div>
      <div class="view-all-container">
        <router-link to="/blog" class="view-all-btn">
          View All Articles
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="5" y1="12" x2="19" y2="12"></line>
            <polyline points="12 5 19 12 12 19"></polyline>
          </svg>
        </router-link>
      </div>
    </section>

    <section class="mission-section">
      <div class="mission-content">
        <div class="mission-icon">
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
            <path d="M12 2L2 7l10 5 10-5-10-5z"></path>
            <path d="M2 17l10 5 10-5M2 12l10 5 10-5"></path>
          </svg>
        </div>
        <h2 class="mission-title">Research Mission</h2>
        <p class="mission-text">
          My work examines the complex systems of power that shape modern society — from
          corporate structures to industrial complexes. Through rigorous research and accessible
          writing, I aim to illuminate these often-hidden mechanisms and foster informed dialogue
          about creating more equitable systems.
        </p>
        <div class="mission-areas">
          <div class="area-tag">Corporate Power Analysis</div>
          <div class="area-tag">Systemic Structures</div>
          <div class="area-tag">Social Impact</div>
          <div class="area-tag">Historical Context</div>
          <a href="https://benevolism.com" target="_blank" rel="noopener" class="area-tag area-tag--gold">
            ⚖ Benevolism Founder
          </a>
          <a href="https://nextupwealthmanagement.com" target="_blank" rel="noopener" class="area-tag area-tag--teal">
            ◈ NextUp Wealth Management
          </a>
          <a href="https://civilneighbr.com" target="_blank" rel="noopener" class="area-tag area-tag--highlight">
            ⬡ Civil Neighbr Founder
          </a>
          <a href="https://thedavidicheir.com" target="_blank" rel="noopener" class="area-tag area-tag--royal">
            ♛ Davidic Heir · thedavidicheir.com
          </a>
        </div>
      </div>
    </section>

    <section class="cta-section">
      <div class="cta-content">
        <h2 class="cta-title">Stay Connected</h2>
        <p class="cta-description">
          Follow along for new research, book releases, and thoughtful analysis
        </p>
        <div class="cta-buttons">
          <router-link to="/blog" class="cta-btn primary">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"></path>
              <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"></path>
            </svg>
            Read the Blog
          </router-link>
          <router-link to="/books" class="cta-btn secondary">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path>
              <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path>
            </svg>
            Explore Books
          </router-link>
          <router-link to="/scripture" class="cta-btn scripture-btn">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"></path>
              <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"></path>
            </svg>
            Scripture Research
          </router-link>
          <a href="https://thedavidicheir.com" target="_blank" rel="noopener" class="cta-btn davidic-btn">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M3 8l7.89 5.26a2 2 0 0 0 2.22 0L21 8M5 19h14a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2z"></path>
            </svg>
            The Davidic Heir
          </a>
          <a href="https://benevolism.com" target="_blank" rel="noopener" class="cta-btn benv-btn">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M12 2L2 7l10 5 10-5-10-5z"></path>
              <path d="M2 17l10 5 10-5M2 12l10 5 10-5"></path>
            </svg>
            Explore Benevolism
          </a>
          <a href="https://nextupwealthmanagement.com" target="_blank" rel="noopener" class="cta-btn nextup-btn">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="22 7 13.5 15.5 8.5 10.5 2 17"></polyline>
              <polyline points="16 7 22 7 22 13"></polyline>
            </svg>
            NextUp Wealth Management
          </a>
          <a href="https://civilneighbr.com" target="_blank" rel="noopener" class="cta-btn cn-btn">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
              <circle cx="9" cy="7" r="4"></circle>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
              <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
            </svg>
            Join Civil Neighbr
          </a>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { db } from '../firebase'

export default {
  name: 'AboutView',
  data() {
    return {
      activeQuoteIndex: 0,
      quoteInterval: null,
      postsLoading: false,
      quotesLoading: false,
      recentPosts: [],
      quotes: [],

      identityPillars: [
        { icon: '♛', title: 'Davidic Lineage', text: 'A descendant in the royal line of King David — heirs to a covenant of justice, stewardship, and the responsibility to build righteous institutions.' },
        { icon: '⚖', title: 'Covenant Economics', text: 'Benevolism is the economic expression of Davidic stewardship: wealth held in trust, distributed with fairness, and never extracted at the expense of those being served.' },
        { icon: '📖', title: 'Scripture as Physics', text: "The canon is not myth — it is a multidimensional record. From Ezekiel's wheels to Paul's four spatial dimensions, Scripture describes a cosmos that modern physics is only beginning to map." },
        { icon: '🤝', title: 'Neighbor as Covenant', text: 'Civil Neighbr is the practical outworking of Leviticus 19:18 — love your neighbor — expressed through technology and community infrastructure.' }
      ],

      nextupTenets: [
        { glyph: 'Σ', title: 'Compound extraction is real', text: 'The financial system transfers wealth from those who need it to those who control it — by design, not accident. NextUp operates inside this system to understand and redirect it.' },
        { glyph: 'λ', title: 'Zero-emotion execution', text: 'Algorithms don\'t panic, greed, or discriminate. That same ruthless objectivity is the model for what a just financial system must become.' },
        { glyph: '∂', title: 'Surplus funds the mission', text: 'Every dollar generated by NextUp is aimed at a single target: funding the research, infrastructure, and philosophy of Benevolism.' },
      ],

      nextupTerminalLines: [
        { prefix: '>>>', text: 'engine.run(strategy="benevolism_v3")', type: 'cmd' },
        { prefix: '[✓]', text: 'Execution: zero-emotion, full-precision', type: 'success' },
        { prefix: '[✓]', text: 'Surplus routing: benevolism.fund()', type: 'success' },
        { prefix: '[→]', text: 'Target: financial extraction margin = 0', type: 'info' },
        { prefix: '[—]', text: 'External access: none', type: 'dim' },
        { prefix: '[—]', text: 'Horizon: indefinite', type: 'dim' },
      ],

      nextupLedger: [
        { figure: '$12B+', label: 'Overdraft fees annually', note: 'Collected from the customers least able to afford them.', color: '#ff003c' },
        { figure: '300%+', label: 'Payday loan APR', note: 'The cost of short-term credit for those with no alternative.', color: '#ff003c' },
        { figure: '~$190K', label: 'Lost per retiree to fees', note: 'The compound cost of a 1% annual fee over 30 years vs. index.', color: '#ffd60a' },
        { figure: '28%', label: 'US households underbanked', note: 'Excluded from mainstream financial systems entirely.', color: '#ffd60a' },
      ],

      cfPrinciples: [
        { title: 'Fair, Not Extractive', text: 'Honest, transparent pricing over hidden fees and lock-in — software that earns its price instead of extracting one.' },
        { title: 'Operated, Not Just Owned', text: 'We stay hands-on with every product long after launch — support, updates, and roadmap included.' },
        { title: 'Shared Engineering Discipline', text: 'Every company we build draws on the same modern stack, security standards, and attention to detail.' },
      ],

      cfStats: [
        { num: '07', label: 'Portfolio & Nonprofit' },
        { num: '2024', label: 'Founded' },
        { num: '100%', label: 'Independently Operated' },
      ],

      cfPortfolio: [
        { name: 'Schoolz Connect', category: 'K–12 Education', link: 'https://schoolzconnect.com/' },
        { name: 'Corporate Connectionz', category: 'Workplace & Culture', link: 'https://corporateconnectionz.com/' },
        { name: 'Heartline Hub', category: 'Senior Living', link: 'https://heartlinehub.com/' },
        { name: 'Wrenzo Suite', category: 'Auto Repair Shops', link: 'https://wrenzosuite.com/' },
        { name: 'ClubLink Software', category: 'Private Club Management', link: 'https://clublinksoftware.com/' },
        { name: 'Club Trailz', category: 'Reciprocal Club Travel', link: 'https://clubtrailz.com/' },
        { name: 'Civil Neighbr', category: 'Nonprofit · Community Aid', link: 'https://civilneighbr.com/login' },
      ],

      benevolismSeries: [
        {
          volume: 'I', theme: 'theme-navy',
          title: 'The Benevolent Economy',
          titleLine1: 'THE', titleLine2: 'BENEVOLENT ECONOMY',
          subtitle: 'How Wealth Grows When Businesses Take Less',
          description: 'The founding treatise of the Benevolism movement. Six parts, sixteen chapters, and a complete doctrine for economic design that prioritizes participation, fairness, and shared prosperity over extraction.',
          link: 'https://www.amazon.com/dp/B0H5G63GSY',
        },
        {
          volume: 'II', theme: 'theme-emerald',
          title: 'The Benevolent Cure',
          titleLine1: 'THE', titleLine2: 'BENEVOLENT CURE',
          subtitle: 'How Benevolism Would Transform Healthcare, Restore Affordability, and Heal the People the System Has Abandoned',
          description: 'Four structural layers, ten binding laws, and a five-part Healthcare Pledge for transforming a system built on extraction into one built for healing.',
          link: 'https://www.amazon.com/dp/B0H5YML4DJ',
        },
        {
          volume: 'III', theme: 'theme-bronze',
          title: 'The Benevolent Bank',
          titleLine1: 'THE', titleLine2: 'BENEVOLENT BANK',
          subtitle: 'How Benevolism Would Transform Finance, Democratize Wealth, and Free Millions From the Machinery of Financial Extraction',
          description: 'Five founding principles, ten binding laws, and a five-part Financial Pledge for transforming a system built on extraction into one built to serve the people who depend on it.',
          link: 'https://www.amazon.com/BENEVOLENT-BANK-Benevolism-Democratize-Extraction/dp/B0H6M1Z43C',
        },
        {
          volume: 'IV', theme: 'theme-violet',
          title: 'The Benevolent Classroom',
          titleLine1: 'THE', titleLine2: 'BENEVOLENT CLASSROOM',
          subtitle: 'How Benevolism Would Transform Education, End the Tuition Crisis, and Make Learning a Right for Every Human Being',
          description: 'Five founding principles, ten binding laws, and a five-part Education Pledge for transforming a system built on extraction into one built for learning.',
          link: 'https://www.amazon.com/dp/B0H93BGQPK',
        },
        {
          volume: 'V', theme: 'theme-steel',
          title: 'The Benevolent Machine',
          titleLine1: 'THE', titleLine2: 'BENEVOLENT MACHINE',
          subtitle: 'How Benevolism Would Transform Technology, Reclaim the Internet, and Build AI That Serves Humanity',
          description: 'Five principles of benevolent platform design, ten binding laws, and a five-part Technology Pledge for transforming a technology sector built on extraction into one built to serve the people who use it.',
          link: 'https://www.amazon.com/dp/B0H9GRP117/ref=sr_1_6?dib=eyJ2IjoiMSJ9.3vO5FTKeYX3m5Fi9Jlxqkotuc4o3FSqlJhG-J3tQIceS5yk6HOlA5YjbfHg840PXqhOhXbvv3D1GCvsgrKS-dhDZheFrh6d8O_awtQ-ICWmSri2KpRbA_u-OCYLBbNFznKXelt17jZSVHv9DQUu8oUGEDyrxZJCXcb4H_Ds7Wu3Vpj5s1xnlp6fKCU3JUxxQiLb6M1os20x-iVclJm_CqJJ_-MaxDV7OWqkrDXnUQVA.5mZCdkHQUi1XkwGoFhtIyshq92Pmw2Pl-K4zDFQ-lXc&dib_tag=se&qid=1784463372&refinements=p_27%3AEnrico%2BMichael%2BSevere&s=books&sr=1-6&text=Enrico%2BMichael%2BSevere',
        },
        {
          volume: 'VI', theme: 'theme-crimson',
          title: 'The Benevolent State',
          titleLine1: 'THE', titleLine2: 'BENEVOLENT STATE',
          subtitle: 'How Benevolism Would Transform Public Policy, Rebuild the Social Contract, and Create a Government That Serves Everyone',
          description: 'The sixth volume in the Benevolism Series. A diagnosis of how taxation, regulation, and welfare have been captured by extraction logic — and a full doctrine for a government designed to serve the people it governs.',
          link: 'https://www.amazon.com/dp/B0HCF58PJ1',
        },
        {
          volume: 'VII', theme: 'theme-teal',
          title: 'The Benevolent Home',
          titleLine1: 'THE', titleLine2: 'BENEVOLENT HOME',
          subtitle: 'How Benevolism Would End the Housing Crisis, Make Shelter Affordable for Every Family, and Build Communities That Last',
          description: 'The seventh volume in the Benevolism Series. Housing is the physical foundation of a human life — a full prescription for supply, ownership, and tenant justice that puts shelter before speculation.',
          link: 'https://www.amazon.com/dp/B0HCDVBFHK',
        },
        {
          volume: 'VIII', theme: 'theme-amber',
          title: 'The Benevolent Journey',
          titleLine1: 'THE', titleLine2: 'BENEVOLENT JOURNEY',
          subtitle: 'How Benevolism Would Transform Transportation, Free People From Car Dependency, and Build Mobility as a Public Right',
          description: 'The eighth volume in the Benevolism Series. A full doctrine for transit, pricing, and land use that moves everyone toward the lives they deserve to live, not just those who can afford a car.',
          link: 'https://www.amazon.com/dp/B0HDQVQNZJ',
        },
        {
          volume: 'IX', theme: 'theme-cyan',
          title: 'The Benevolent Grid',
          titleLine1: 'THE', titleLine2: 'BENEVOLENT GRID',
          subtitle: 'How Benevolism Would Transform Energy, Democratize Renewable Power, and Make Clean Energy a Right for Every Person',
          description: 'The ninth volume in the Benevolism Series. A full doctrine for utility regulation and honest pricing that turns the fastest cost decline in energy history into lower bills for everyone.',
          link: 'https://www.amazon.com/dp/B0HFD5XTTT',
        },
        {
          volume: 'X', theme: 'theme-olive',
          title: 'The Benevolent Harvest',
          titleLine1: 'THE', titleLine2: 'BENEVOLENT HARVEST',
          subtitle: 'How Benevolism Would Transform Food Systems, End Grocery Extraction, and Guarantee Nourishing Food for Every Person',
          description: 'The tenth volume in the Benevolism Series. A full doctrine for farm-to-fork fairness, from farmer income to grocery pricing to school meals.',
          link: 'https://www.amazon.com/dp/B0HHMD7SJ3',
        },
        {
          volume: 'XI', theme: 'theme-indigo',
          title: 'The Benevolent Workplace',
          titleLine1: 'THE', titleLine2: 'BENEVOLENT WORKPLACE',
          subtitle: 'How Benevolism Would Transform Labor, Restore Worker Power, and Make Every Job a Source of Dignity and Prosperity',
          description: 'The eleventh volume in the Benevolism Series. A full doctrine for living wages, collective bargaining, and gig worker rights that closes the forty-year gap between productivity and pay.',
          link: 'https://www.amazon.com/dp/B0HHM7K9FP',
        },
        {
          volume: 'XII', theme: 'theme-rose',
          title: 'The Benevolent Marketplace',
          titleLine1: 'THE', titleLine2: 'BENEVOLENT MARKETPLACE',
          subtitle: 'How Benevolism Would Transform Retail, Build Ethical Supply Chains, and Make Commerce Honest Again',
          description: 'The twelfth volume in the Benevolism Series. A full doctrine for honest markups, ethical sourcing, and retail labor that trades honestly at every link in the chain.',
          link: 'https://www.amazon.com/dp/B0HJH52F6P',
        },
        {
          volume: 'XIII', theme: 'theme-plum',
          title: 'The Benevolent Stage',
          titleLine1: 'THE', titleLine2: 'BENEVOLENT STAGE',
          subtitle: 'How Benevolism Would Transform Entertainment, Restore Creator Rights, and Make Art Pay the People Who Make It',
          description: 'The thirteenth volume in the Benevolism Series. A full doctrine for honest royalties, creator ownership, and consent-based AI that pays the people who make our culture.',
          link: 'https://www.amazon.com/dp/B0HJFWHMVR',
        },
        {
          volume: 'XIV', theme: 'theme-forest',
          title: 'The Benevolent Ground',
          titleLine1: 'THE', titleLine2: 'BENEVOLENT GROUND',
          subtitle: 'How Benevolism Would Transform Real Estate Development, Build Community Wealth, and Make Land Work for the People Who Live on It',
          description: 'The fourteenth volume in the Benevolism Series. A full doctrine for community land trusts and land value capture that builds community wealth instead of extracting it.',
          link: 'https://www.amazon.com/dp/B0HJFXKHBJ',
        },      ],

      benevolismConvictions: [
        { title: 'Extraction is a design choice', text: 'Structures that drain communities of wealth are not natural laws — they are decisions that can be redesigned.' },
        { title: 'Businesses that take less ultimately grow more', text: 'Taking less is a trade of per-unit margin for total volume — the logic of shared prosperity over positional accumulation.' },
        { title: 'The middle class is the engine of a healthy economy', text: 'Benevolism insists on protecting and expanding the middle class as an economic design principle, not mere policy.' },
        { title: 'Low-friction pricing expands total economic participation', text: 'When participation is cheap and transparent, markets grow deeper, more diverse, and more productive.' },
        { title: 'Benevolent entrepreneurship is the most powerful force for shared prosperity', text: 'Entrepreneurs who design for participant flourishing build moats that matter: trust, loyalty, and ecosystem health.' }
      ],

      tenLawsPreview: [
        { num: 'I',    name: 'The Law of Minimal Extraction' },
        { num: 'II',   name: 'The Law of Radical Transparency' },
        { num: 'III',  name: 'The Law of Participant Priority' },
        { num: 'IV',   name: 'The Law of Participatory Access' },
        { num: 'V',    name: 'The Law of Incentive Alignment' },
        { num: 'VI',   name: 'The Law of Governance Integrity' },
        { num: 'VII',  name: 'The Law of Community Sovereignty' },
        { num: 'VIII', name: 'The Law of Compounding Generosity' },
        { num: 'IX',   name: 'The Law of Long-Term Vision' },
        { num: 'X',    name: 'The Law of Moral Accountability' }
      ],

      covenantSeries: [
        {
          region: 'South America',
          title: 'The Covenant of the Continent',
          subtitle: 'The Documented Indictment and the Benevolism Redesign for South America',
          description: 'Nation by nation, system by system, fact by fact — a documented indictment of extraction across South America paired with the Benevolism covenant redesign for each country.',
          cover: require('../assets/The Covenant Of The Continent.png'),
          link: 'https://www.amazon.com/dp/B0H825RK97',
        },
        {
          region: 'North America',
          title: 'A Covenant for the North',
          subtitle: 'The Documented Indictment and the Benevolism Redesign for North America',
          description: 'The companion volume, applying the same documented indictment and covenant redesign to the United States, Canada, Mexico, Central America, and the Caribbean.',
          cover: require('../assets/A Covenant For The North.png'),
          link: 'https://www.amazon.com/dp/B0H83TSSCL',
        },
        {
          region: 'Africa',
          title: 'A Covenant for Africa',
          subtitle: 'The Documented Indictment and the Benevolism Redesign for Africa',
          description: 'Nation by nation, system by system, fact by fact — a documented indictment of extraction across Africa, from the slave trade to illicit capital flight, paired with the Benevolism covenant redesign for each country.',
          cover: require('../assets/A Covenant For Africa.png'),
          link: 'https://www.amazon.com/dp/B0H87DZPYZ',
        },
        {
          region: 'Europe',
          title: 'A Covenant for Europe',
          subtitle: 'The Documented Indictment and the Benevolism Redesign for Europe',
          description: 'Nation by nation, system by system, fact by fact — a documented indictment of extraction across Europe, from colonial debt to austerity, paired with the Benevolism covenant redesign for each country.',
          cover: require('../assets/A Covenant For Europe.png'),
          link: 'https://www.amazon.com/dp/B0H8J4SKXZ',
        },
        {
          region: 'Asia',
          title: 'A Covenant for Asia',
          subtitle: 'The Documented Indictment and the Benevolism Redesign for Asia',
          description: 'Nation by nation, system by system, fact by fact — a documented indictment of extraction across Asia, from supply chain exploitation to caste-based exclusion, paired with the Benevolism covenant redesign for each country.',
          cover: require('../assets/A Covenant For Asia.png'),
          link: 'https://www.amazon.com/dp/B0H8JKT77Y',
        },
        {
          region: 'Pacific',
          title: 'A Covenant for the Pacific',
          subtitle: 'The Documented Indictment and the Benevolism Redesign for Australia, New Zealand, and the Pacific Islands',
          description: 'Nation by nation, system by system, fact by fact — a documented indictment of extraction across Australia, New Zealand, and the Pacific Islands, paired with the Benevolism covenant redesign for each country.',
          cover: require('../assets/A Covenant For The Pacific.png'),
          link: 'https://www.amazon.com/dp/B0H8KWFPS4',
        },
      ],

      books: [
        { title: "Obsolescence: How Companies Use It To Get Wealthier", author: "Enrico Michael Severe", cover: require('../assets/OBSOLESCENCE.png'), link: "https://www.amazon.com/Obsolescense-How-Companies-Use-Wealthier/dp/B0FBHNMK3Y", description: "An exploration of planned obsolescence and corporate profit strategies" },
        { title: "The Prison Industrial Complex", author: "Enrico Michael Severe", cover: require('../assets/THE PRISON.png'), link: "https://www.amazon.com/Prison-Industrial-Complex-Comprehensive-Exploration/dp/B0FBHXBJ99", description: "A comprehensive examination of mass incarceration and profit" },
        { title: "Military Industrial Complex", author: "Enrico Michael Severe", cover: require('../assets/Military Industrial Complex.png'), link: "https://www.amazon.com/Military-Industrial-Complex-Power-Policy-Impact/dp/B0FG7ZHZ27", description: "Analyzing the intersection of defense, policy, and corporate power" },
        { title: "Plundered Republics: Corporate Imperialism In South America", author: "Enrico Michael Severe", cover: require('../assets/Plundered Republics.png'), link: "https://www.amazon.com/Plundered-Republics-Corporate-Imperialism-America/dp/B0FG81W37X", description: "Uncovering corporate exploitation across South American nations" }
      ]
    }
  },

  computed: {
    totalBookCount() {
      return this.books.length + this.benevolismSeries.length + this.covenantSeries.length
    }
  },

  mounted() {
    this.startQuoteCarousel()
    this.loadRecentPosts()
    this.loadQuotes()
  },

  beforeUnmount() {
    if (this.quoteInterval) clearInterval(this.quoteInterval)
  },

  methods: {
    startQuoteCarousel() {
      this.quoteInterval = setInterval(() => {
        if (this.quotes.length > 0) {
          this.activeQuoteIndex = (this.activeQuoteIndex + 1) % this.quotes.length
        }
      }, 6000)
    },

    async loadRecentPosts() {
      this.postsLoading = true
      try {
        const snapshot = await db.collection('posts').where('status', '==', 'published').orderBy('createdAt', 'desc').limit(3).get()
        this.recentPosts = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))
      } catch (error) {
        console.error('Error loading posts:', error)
      } finally {
        this.postsLoading = false
      }
    },

    async loadQuotes() {
      this.quotesLoading = true
      try {
        const snapshot = await db.collection('quotes').orderBy('createdAt', 'desc').get()
        this.quotes = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))
      } catch (error) {
        console.error('Error loading quotes:', error)
      } finally {
        this.quotesLoading = false
      }
    },

    formatDate(timestamp) {
      if (!timestamp) return 'Recently'
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp)
      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
    },

    truncate(text, length) {
      if (!text) return ''
      const stripped = text.replace(/[#*>`\-]/g, '').trim()
      return stripped.length > length ? stripped.substring(0, length) + '...' : stripped
    },

    estimateReadingTime(content) {
      if (!content) return 1
      const words = content.trim().split(/\s+/).length
      return Math.max(1, Math.ceil(words / 200))
    }
  }
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Crimson+Pro:wght@400;600;700&family=Inter:wght@400;500;600;700&family=Playfair+Display:wght@700;900&family=Cinzel:wght@400;600;700&family=JetBrains+Mono:wght@300;400;500&display=swap');

.about-container { min-height:100vh; background:#0d1117; }

.hero-section { position:relative; min-height:85vh; background:linear-gradient(135deg,#1a1a2e 0%,#16213e 50%,#0f3460 100%); display:flex; flex-direction:column; align-items:center; justify-content:center; padding:80px 24px; overflow:hidden; }
.hero-overlay { position:absolute; inset:0; background:radial-gradient(circle at 20% 50%,rgba(139,92,246,0.15) 0%,transparent 50%),radial-gradient(circle at 80% 80%,rgba(59,130,246,0.15) 0%,transparent 50%); pointer-events:none; }
.hero-content { position:relative; text-align:center; max-width:900px; z-index:1; }
.profile-circle { width:180px; height:180px; margin:0 auto 32px; border-radius:50%; overflow:hidden; border:5px solid rgba(255,255,255,0.2); box-shadow:0 20px 60px rgba(0,0,0,0.4); animation:fadeInScale 1s ease-out; }
@keyframes fadeInScale { from{opacity:0;transform:scale(0.8)} to{opacity:1;transform:scale(1)} }
.profile-img { width:100%; height:100%; object-fit:cover; }
.hero-title { font-family:'Crimson Pro',serif; font-size:64px; font-weight:700; color:white; margin-bottom:12px; letter-spacing:-1px; animation:fadeInUp 1s ease-out 0.2s both; }
.hero-subtitle { font-family:'Inter',sans-serif; font-size:20px; font-weight:500; color:rgba(255,255,255,0.85); margin-bottom:20px; animation:fadeInUp 1s ease-out 0.4s both; letter-spacing:1px; }
.hero-lineage { display:flex; align-items:center; justify-content:center; gap:16px; margin-bottom:28px; animation:fadeInUp 1s ease-out 0.55s both; }
.lineage-rule { display:block; width:48px; height:1px; background:linear-gradient(90deg,transparent,#c9a84c); opacity:0.7; }
.lineage-rule:last-child { background:linear-gradient(90deg,#c9a84c,transparent); }
.lineage-text { font-family:'Cinzel',serif; font-size:13px; font-weight:600; letter-spacing:0.2em; color:#c9a84c; text-transform:uppercase; display:flex; align-items:center; gap:10px; }
.lineage-crown { font-size:16px; filter:sepia(1) hue-rotate(15deg) saturate(3) brightness(1.2); }
.hero-description { font-family:'Inter',sans-serif; font-size:18px; line-height:1.7; color:rgba(255,255,255,0.8); max-width:700px; margin:0 auto 48px; animation:fadeInUp 1s ease-out 0.6s both; }
@keyframes fadeInUp { from{opacity:0;transform:translateY(30px)} to{opacity:1;transform:translateY(0)} }
.hero-stats { display:flex; justify-content:center; align-items:center; gap:48px; margin-top:48px; animation:fadeInUp 1s ease-out 0.8s both; }
.stat-item { display:flex; flex-direction:column; align-items:center; gap:8px; }
.stat-number { font-family:'Crimson Pro',serif; font-size:48px; font-weight:700; color:white; line-height:1; }
.stat-label { font-family:'Inter',sans-serif; font-size:14px; font-weight:500; color:rgba(255,255,255,0.7); text-transform:uppercase; letter-spacing:1px; }
.stat-divider { width:1px; height:60px; background:rgba(255,255,255,0.2); }
.scroll-indicator { position:absolute; bottom:32px; left:50%; transform:translateX(-50%); color:rgba(255,255,255,0.6); animation:bounce 2s infinite; }
@keyframes bounce { 0%,20%,50%,80%,100%{transform:translateX(-50%) translateY(0)} 40%{transform:translateX(-50%) translateY(-10px)} 60%{transform:translateX(-50%) translateY(-5px)} }

.lineage-section { background:linear-gradient(160deg,#0d1117 0%,#0b1628 60%,#0d1117 100%); padding:96px 24px; border-top:1px solid rgba(201,168,76,0.15); border-bottom:1px solid rgba(201,168,76,0.15); position:relative; overflow:hidden; }
.lineage-section::before { content:'♛'; position:absolute; right:-40px; top:-40px; font-size:280px; color:rgba(201,168,76,0.025); pointer-events:none; line-height:1; }
.lineage-inner { max-width:1200px; margin:0 auto; display:grid; grid-template-columns:1fr 1fr; gap:80px; align-items:start; }
.lin-eyebrow { font-family:'Inter',sans-serif; font-size:10px; font-weight:700; letter-spacing:0.28em; text-transform:uppercase; color:#c9a84c; margin-bottom:16px; display:flex; align-items:center; gap:12px; }
.lin-eyebrow::before { content:''; display:block; width:32px; height:1px; background:#c9a84c; }
.lin-title { font-family:'Cinzel',serif; font-size:clamp(28px,3.5vw,48px); font-weight:700; color:#f5f0e8; line-height:1.15; margin:0 0 28px; letter-spacing:0.02em; }
.lin-body { font-family:'Playfair Display',serif; font-size:18px; line-height:1.8; color:rgba(245,240,232,0.82); margin:0 0 18px; }
.lin-body--secondary { font-family:'Inter',sans-serif; font-size:15px; color:#718096; font-weight:300; }
.lin-badges { display:flex; gap:10px; flex-wrap:wrap; margin-top:32px; }
.lin-badge { display:inline-flex; align-items:center; gap:8px; padding:10px 18px; font-family:'Inter',sans-serif; font-size:12px; font-weight:700; letter-spacing:0.06em; text-decoration:none; transition:all 0.2s; }
.lin-badge--gold { background:rgba(201,168,76,0.12); border:1px solid rgba(201,168,76,0.35); color:#c9a84c; }
.lin-badge--gold:hover { background:rgba(201,168,76,0.2); }
.lin-badge-crown { font-size:15px; filter:sepia(1) hue-rotate(15deg) saturate(3) brightness(1.2); }
.lin-badge--indigo { background:rgba(99,102,241,0.1); border:1px solid rgba(99,102,241,0.3); color:#818cf8; }
.lin-badge--indigo:hover { background:rgba(99,102,241,0.2); }
.lin-badge--link { background:transparent; border:1px solid rgba(255,255,255,0.1); color:#718096; }
.lin-badge--link:hover { border-color:rgba(201,168,76,0.3); color:#c9a84c; }
.psalm-card { background:linear-gradient(160deg,#0d1f3c 0%,#0b1628 60%,#071020 100%); border:1px solid rgba(201,168,76,0.35); padding:36px 32px; text-align:center; margin-bottom:32px; position:relative; }
.psalm-card::before { content:''; position:absolute; inset:6px; border:1px solid rgba(201,168,76,0.12); pointer-events:none; }
.psalm-card__rule { width:60%; height:1px; background:linear-gradient(90deg,transparent,#c9a84c,transparent); margin:0 auto 20px; }
.psalm-card__crown { font-size:28px; filter:sepia(1) hue-rotate(15deg) saturate(3) brightness(1.2); margin-bottom:18px; display:block; }
.psalm-card__quote { font-family:'Playfair Display',serif; font-size:18px; font-style:italic; line-height:1.7; color:#f5f0e8; margin:0 0 18px; }
.psalm-card__ref { font-family:'Cinzel',serif; font-size:11px; font-weight:600; letter-spacing:0.15em; color:#c9a84c; text-transform:uppercase; margin-bottom:20px; display:block; }
.lin-pillars { display:flex; flex-direction:column; gap:0; border:1px solid rgba(201,168,76,0.12); margin-bottom:28px; }
.lin-pillar { display:flex; gap:16px; padding:18px 22px; border-bottom:1px solid rgba(201,168,76,0.06); transition:background 0.2s; }
.lin-pillar:last-child { border-bottom:none; }
.lin-pillar:hover { background:rgba(201,168,76,0.04); }
.lin-pillar__icon { font-size:20px; flex-shrink:0; width:28px; text-align:center; padding-top:2px; }
.lin-pillar__title { font-family:'Playfair Display',serif; font-size:15px; font-weight:700; color:#f5f0e8; margin-bottom:5px; }
.lin-pillar__text { font-family:'Inter',sans-serif; font-size:12px; line-height:1.65; color:#718096; font-weight:300; }
.davidic-heir-card { display:flex; align-items:flex-start; gap:20px; background:linear-gradient(135deg,rgba(139,92,246,0.08) 0%,rgba(99,102,241,0.04) 100%); border:1px solid rgba(139,92,246,0.3); border-left:3px solid #8b5cf6; padding:24px; margin-bottom:28px; text-decoration:none; transition:border-color 0.2s,background 0.2s; }
.davidic-heir-card:hover { border-color:rgba(139,92,246,0.6); background:linear-gradient(135deg,rgba(139,92,246,0.12) 0%,rgba(99,102,241,0.07) 100%); }
.davidic-heir-card__crown { font-size:28px; filter:sepia(1) hue-rotate(200deg) saturate(2) brightness(1.3); flex-shrink:0; margin-top:2px; }
.davidic-heir-card__eyebrow { font-family:'Inter',sans-serif; font-size:9px; font-weight:700; letter-spacing:0.22em; text-transform:uppercase; color:#a78bfa; margin-bottom:6px; }
.davidic-heir-card__title { font-family:'Cinzel',serif; font-size:18px; font-weight:700; color:#f5f0e8; margin-bottom:10px; letter-spacing:0.04em; }
.davidic-heir-card__text { font-family:'Inter',sans-serif; font-size:13px; line-height:1.7; color:rgba(245,240,232,0.65); margin:0 0 14px; }
.davidic-heir-card__link { font-family:'Cinzel',serif; font-size:11px; font-weight:600; letter-spacing:0.1em; color:#a78bfa; display:flex; align-items:center; gap:6px; transition:gap 0.2s; }
.davidic-heir-card:hover .davidic-heir-card__link { gap:10px; }
.lin-scripture-cta { background:rgba(99,102,241,0.06); border:1px solid rgba(99,102,241,0.2); border-left:3px solid #6366f1; padding:24px; }
.lin-scripture-cta__tag { display:inline-flex; align-items:center; gap:7px; font-family:'Inter',sans-serif; font-size:10px; font-weight:700; letter-spacing:0.18em; text-transform:uppercase; color:#818cf8; margin-bottom:12px; }
.lin-scripture-cta__text { font-family:'Inter',sans-serif; font-size:14px; line-height:1.7; color:#9ca3af; margin:0 0 18px; }
.lin-scripture-cta__btn { display:inline-flex; align-items:center; gap:9px; padding:12px 22px; background:#6366f1; color:#fff; font-family:'Inter',sans-serif; font-size:13px; font-weight:700; text-decoration:none; letter-spacing:0.05em; transition:background 0.2s,transform 0.2s; }
.lin-scripture-cta__btn:hover { background:#818cf8; transform:translateY(-2px); }

.benevolism-section { position:relative; background:#0b1628; padding:80px 24px; overflow:hidden; }
.benv-bg-grid { position:absolute; inset:0; background-image:linear-gradient(rgba(201,168,76,0.04) 1px,transparent 1px),linear-gradient(90deg,rgba(201,168,76,0.04) 1px,transparent 1px); background-size:60px 60px; pointer-events:none; }
.benv-glow { position:absolute; top:-200px; right:-200px; width:700px; height:700px; background:radial-gradient(circle,rgba(201,168,76,0.08) 0%,transparent 70%); pointer-events:none; }
.benv-inner { position:relative; z-index:2; max-width:1200px; margin:0 auto; display:grid; grid-template-columns:1fr 1fr; gap:80px; align-items:start; }
.benv-eyebrow { font-family:'Inter',sans-serif; font-size:11px; font-weight:700; letter-spacing:3px; text-transform:uppercase; color:#c9a84c; margin-bottom:16px; display:flex; align-items:center; gap:12px; }
.benv-eyebrow::before { content:''; display:block; width:32px; height:1px; background:#c9a84c; }
.benv-logo-row { display:flex; align-items:center; gap:14px; margin-bottom:28px; }
.benv-mark { width:52px; height:52px; background:#c9a84c; color:#0b1628; display:flex; align-items:center; justify-content:center; font-family:'Playfair Display',serif; font-weight:700; font-size:26px; flex-shrink:0; }
.benv-name { font-family:'Inter',sans-serif; font-size:28px; font-weight:600; letter-spacing:0.18em; color:#f5f0e8; margin:0; }
.benv-heading { font-family:'Playfair Display',serif; font-size:32px; font-weight:700; color:#f5f0e8; line-height:1.2; margin:0 0 24px; }
.benv-heading em { color:#c9a84c; font-style:italic; }
.benv-body { font-family:'Inter',sans-serif; font-size:16px; line-height:1.75; color:#c8c0b0; margin:0 0 16px; font-weight:300; }
.benv-body--secondary { color:#718096; font-size:15px; }
.benv-convictions { display:flex; flex-direction:column; gap:0; margin:32px 0; border:1px solid rgba(201,168,76,0.12); }
.benv-conviction { display:flex; gap:16px; padding:18px 20px; border-bottom:1px solid rgba(201,168,76,0.08); transition:background 0.2s; }
.benv-conviction:last-child { border-bottom:none; }
.benv-conviction:hover { background:rgba(201,168,76,0.04); }
.benv-conviction-num { font-family:'Inter',sans-serif; font-size:10px; font-weight:700; color:#8a6e2a; letter-spacing:0.1em; padding-top:3px; flex-shrink:0; width:24px; }
.benv-conviction-title { font-family:'Playfair Display',serif; font-size:15px; font-weight:600; color:#f5f0e8; margin-bottom:4px; }
.benv-conviction-text { font-family:'Inter',sans-serif; font-size:13px; line-height:1.6; color:#718096; font-weight:300; }
.benv-founder-badge { display:inline-flex; align-items:center; gap:10px; background:rgba(201,168,76,0.1); border:1px solid rgba(201,168,76,0.3); padding:10px 20px; font-family:'Inter',sans-serif; font-size:13px; font-weight:600; color:#c9a84c; }
.benv-badge-dot { width:8px; height:8px; border-radius:50%; background:#c9a84c; animation:benvPulse 2s infinite; flex-shrink:0; }
@keyframes benvPulse { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:0.5;transform:scale(0.8)} }
.benv-book-card { width:200px; min-height:290px; background:linear-gradient(160deg,#0d1f3c 0%,#0b1628 60%,#071020 100%); border:1px solid rgba(201,168,76,0.4); box-shadow:0 0 0 1px rgba(201,168,76,0.1),8px 8px 32px rgba(0,0,0,0.6),inset 0 0 60px rgba(201,168,76,0.03); padding:24px 18px; display:flex; flex-direction:column; align-items:center; text-align:center; gap:7px; transform:rotate(-2deg); transition:transform 0.3s; position:relative; margin:0 auto 32px; }
.benv-book-card::before { content:''; position:absolute; inset:6px; border:1px solid rgba(201,168,76,0.2); pointer-events:none; }
.benv-book-card:hover { transform:rotate(0deg) scale(1.02); }
.benv-book-crown { font-size:28px; filter:sepia(1) hue-rotate(15deg) saturate(3) brightness(1.2); }
.benv-book-title-text { font-family:'Playfair Display',serif; font-size:11px; font-weight:400; color:#e8cc80; letter-spacing:0.08em; line-height:1.4; }
.benv-book-title-text strong { font-size:17px; font-weight:900; display:block; letter-spacing:0.03em; }
.benv-book-sub { font-family:'Inter',sans-serif; font-size:8px; color:#c8c0b0; line-height:1.5; font-style:italic; }
.benv-book-divider { width:80%; height:1px; background:linear-gradient(90deg,transparent,#c9a84c,transparent); }
.benv-book-star { color:#c9a84c; font-size:10px; }
.benv-book-treatise { font-family:'Inter',sans-serif; font-size:7px; letter-spacing:0.15em; color:#c8c0b0; }
.benv-book-scales { font-size:24px; filter:sepia(1) hue-rotate(15deg) saturate(3) brightness(1.1); }
.benv-book-author { font-family:'Playfair Display',serif; font-size:10px; font-weight:700; color:#e8cc80; letter-spacing:0.12em; }
.benv-book-heir { font-family:'Inter',sans-serif; font-size:7px; font-style:italic; color:#c8c0b0; }
.benv-book-badge { position:absolute; top:-12px; right:-12px; width:64px; height:64px; background:#c9a84c; border-radius:50%; display:flex; align-items:center; justify-content:center; transform:rotate(15deg); }
.benv-badge-inner { text-align:center; color:#0b1628; }
.benv-badge-top { font-family:'Inter',sans-serif; font-size:6px; font-weight:700; letter-spacing:0.1em; }
.benv-badge-main { font-family:'Playfair Display',serif; font-size:10px; font-weight:900; line-height:1.1; }
.benv-badge-bottom { font-family:'Inter',sans-serif; font-size:7px; font-weight:600; }
.benv-laws-preview { background:rgba(255,255,255,0.04); border:1px solid rgba(201,168,76,0.15); padding:24px; margin-bottom:24px; }
.benv-laws-header { display:flex; align-items:center; gap:8px; font-family:'Inter',sans-serif; font-size:11px; font-weight:700; letter-spacing:0.15em; text-transform:uppercase; color:#c9a84c; margin-bottom:16px; padding-bottom:12px; border-bottom:1px solid rgba(201,168,76,0.12); }
.benv-laws-list { display:grid; grid-template-columns:1fr 1fr; gap:6px 16px; }
.benv-law-item { display:flex; align-items:center; gap:8px; }
.benv-law-num { font-family:'Playfair Display',serif; font-size:11px; font-style:italic; color:#8a6e2a; width:20px; flex-shrink:0; }
.benv-law-name { font-family:'Inter',sans-serif; font-size:11px; color:#c8c0b0; line-height:1.4; }
.benv-cta-card { background:rgba(255,255,255,0.04); border:1px solid rgba(201,168,76,0.25); padding:28px; }
.benv-cta-tag { display:inline-block; font-family:'Inter',sans-serif; font-size:11px; font-weight:700; letter-spacing:1.5px; text-transform:uppercase; color:#c9a84c; background:rgba(201,168,76,0.12); border:1px solid rgba(201,168,76,0.3); padding:5px 12px; margin-bottom:14px; }
.benv-cta-text { font-family:'Inter',sans-serif; font-size:14px; line-height:1.65; color:#c8c0b0; margin:0 0 20px; }
.benv-cta-btn { display:flex; align-items:center; justify-content:center; gap:10px; width:100%; padding:15px 24px; background:#c9a84c; color:#0b1628; font-family:'Inter',sans-serif; font-size:15px; font-weight:700; text-decoration:none; letter-spacing:0.05em; transition:background 0.2s,transform 0.2s; margin-bottom:12px; }
.benv-cta-btn:hover { background:#e8cc80; transform:translateY(-2px); }
.benv-secondary-link { display:block; text-align:center; font-family:'Inter',sans-serif; font-size:13px; color:#718096; text-decoration:none; transition:color 0.2s; }
.benv-secondary-link:hover { color:#c9a84c; }

.nextup-section { position:relative; background:#030b0e; padding:80px 24px; overflow:hidden; border-top:1px solid rgba(0,245,212,0.1); border-bottom:1px solid rgba(0,245,212,0.1); }
.nextup-bg-grid { position:absolute; inset:0; background-image:linear-gradient(rgba(0,245,212,0.03) 1px,transparent 1px),linear-gradient(90deg,rgba(0,245,212,0.03) 1px,transparent 1px); background-size:52px 52px; pointer-events:none; }
.nextup-glow { position:absolute; top:-200px; left:-200px; width:700px; height:700px; background:radial-gradient(circle,rgba(0,245,212,0.06) 0%,transparent 70%); pointer-events:none; }
.nextup-inner { position:relative; z-index:2; max-width:1200px; margin:0 auto; display:grid; grid-template-columns:1fr 1fr; gap:80px; align-items:start; }
.nextup-eyebrow { font-family:'JetBrains Mono',monospace; font-size:10px; font-weight:500; letter-spacing:0.2em; text-transform:uppercase; color:rgba(0,245,212,0.7); margin-bottom:16px; display:flex; align-items:center; gap:12px; }
.nextup-eyebrow::before { content:''; display:block; width:32px; height:1px; background:#00f5d4; }
.nextup-logo-row { display:flex; align-items:center; gap:16px; margin-bottom:12px; }
.nextup-mark { width:56px; height:56px; background:rgba(0,245,212,0.1); border:1px solid rgba(0,245,212,0.3); display:flex; align-items:center; justify-content:center; font-family:'JetBrains Mono',monospace; font-weight:500; font-size:18px; color:#00f5d4; flex-shrink:0; letter-spacing:0.05em; }
.nextup-name-block { display:flex; flex-direction:column; gap:2px; }
.nextup-name { font-family:'Space Grotesk',sans-serif; font-size:32px; font-weight:600; color:#e2f4f0; margin:0; letter-spacing:-0.02em; line-height:1; }
.nextup-name-sub { font-family:'JetBrains Mono',monospace; font-size:11px; color:rgba(226,244,240,0.45); letter-spacing:0.08em; }
.nextup-algo-tag { font-family:'JetBrains Mono',monospace; font-size:10px; letter-spacing:0.12em; color:rgba(0,245,212,0.5); margin-bottom:28px; }
.nextup-accent { color:#00f5d4; }
.nextup-heading { font-family:'Space Grotesk',sans-serif; font-size:28px; font-weight:500; color:#e2f4f0; line-height:1.25; margin:0 0 20px; letter-spacing:-0.02em; }
.nextup-heading em { color:#00f5d4; font-style:normal; }
.nextup-body { font-family:'Inter',sans-serif; font-size:15px; line-height:1.8; color:rgba(226,244,240,0.65); margin:0 0 14px; font-weight:300; }
.nextup-body--secondary { color:rgba(226,244,240,0.4); font-size:14px; }
.nextup-tenets { display:flex; flex-direction:column; margin:28px 0; border:0.5px solid rgba(0,245,212,0.1); }
.nextup-tenet { display:flex; gap:20px; padding:20px 22px; border-bottom:0.5px solid rgba(0,245,212,0.07); transition:background 0.2s; align-items:flex-start; }
.nextup-tenet:last-child { border-bottom:none; }
.nextup-tenet:hover { background:rgba(0,245,212,0.03); }
.nextup-tenet-glyph { font-family:'JetBrains Mono',monospace; font-size:22px; color:rgba(0,245,212,0.2); font-weight:300; flex-shrink:0; width:32px; padding-top:2px; }
.nextup-tenet-title { font-family:'Space Grotesk',sans-serif; font-size:14px; font-weight:500; color:#e2f4f0; margin-bottom:5px; }
.nextup-tenet-text { font-family:'Inter',sans-serif; font-size:12px; line-height:1.65; color:rgba(226,244,240,0.5); font-weight:300; }
.nextup-founder-badge { display:inline-flex; align-items:center; gap:10px; background:rgba(0,245,212,0.06); border:0.5px solid rgba(0,245,212,0.25); padding:10px 20px; font-family:'JetBrains Mono',monospace; font-size:11px; font-weight:500; color:rgba(0,245,212,0.8); letter-spacing:0.06em; }
.nextup-badge-dot { width:6px; height:6px; border-radius:50%; background:#00f5d4; animation:nextupPulse 2s infinite; flex-shrink:0; }
@keyframes nextupPulse { 0%,100%{opacity:1;box-shadow:0 0 0 0 rgba(0,245,212,0.4)} 50%{opacity:0.7;box-shadow:0 0 0 5px rgba(0,245,212,0)} }

.nextup-terminal-card { background:#050f12; border:0.5px solid rgba(0,245,212,0.18); border-radius:8px; overflow:hidden; font-family:'JetBrains Mono',monospace; margin-bottom:24px; }
.nextup-terminal-bar { display:flex; align-items:center; gap:6px; padding:10px 16px; background:#081418; border-bottom:0.5px solid rgba(0,245,212,0.08); }
.nt-dot { width:10px; height:10px; border-radius:50%; }
.nt-red { background:#ff5f57; }
.nt-amber { background:#febc2e; }
.nt-green { background:#28c840; }
.nextup-terminal-title { font-size:11px; color:rgba(226,244,240,0.35); margin-left:8px; letter-spacing:0.04em; flex:1; }
.nt-live { display:flex; align-items:center; gap:5px; font-size:9px; color:rgba(0,245,212,0.6); letter-spacing:0.12em; }
.nt-live-dot { width:5px; height:5px; border-radius:50%; background:#00f5d4; animation:blink 1.2s step-end infinite; }
@keyframes blink { 0%,100%{opacity:1} 50%{opacity:0} }
.nextup-terminal-body { padding:20px; display:flex; flex-direction:column; gap:8px; }
.nt-line { display:flex; gap:14px; font-size:12px; line-height:1.5; }
.nt-prefix { color:rgba(226,244,240,0.3); min-width:32px; }
.nt-line.cmd .nt-text { color:rgba(226,244,240,0.85); }
.nt-line.success .nt-prefix { color:#28c840; }
.nt-line.success .nt-text { color:rgba(57,255,20,0.8); }
.nt-line.info .nt-prefix,.nt-line.info .nt-text { color:#00f5d4; }
.nt-line.dim .nt-text { color:rgba(226,244,240,0.35); }
.nt-cursor { color:#00f5d4; animation:blink 1s step-end infinite; }

.nextup-ledger { background:rgba(5,15,18,0.8); border:0.5px solid rgba(0,245,212,0.12); margin-bottom:24px; }
.nextup-ledger-header { display:flex; align-items:center; gap:8px; padding:14px 18px; border-bottom:0.5px solid rgba(0,245,212,0.08); font-family:'JetBrains Mono',monospace; font-size:10px; letter-spacing:0.12em; color:rgba(0,245,212,0.6); text-transform:uppercase; }
.nextup-ledger-grid { display:grid; grid-template-columns:1fr 1fr; gap:0.5px; background:rgba(0,245,212,0.06); }
.nextup-ledger-item { background:#030b0e; padding:20px 18px; transition:background 0.2s; }
.nextup-ledger-item:hover { background:#060f13; }
.nextup-ledger-fig { font-family:'JetBrains Mono',monospace; font-size:24px; font-weight:500; line-height:1; margin-bottom:6px; }
.nextup-ledger-label { font-family:'Inter',sans-serif; font-size:12px; font-weight:500; color:rgba(226,244,240,0.75); margin-bottom:5px; }
.nextup-ledger-note { font-family:'JetBrains Mono',monospace; font-size:10px; color:rgba(226,244,240,0.35); line-height:1.5; }

.nextup-cta-card { background:rgba(0,245,212,0.04); border:0.5px solid rgba(0,245,212,0.2); padding:28px; }
.nextup-cta-tag { display:inline-block; font-family:'JetBrains Mono',monospace; font-size:10px; font-weight:500; letter-spacing:0.12em; text-transform:uppercase; color:rgba(0,245,212,0.7); background:rgba(0,245,212,0.08); border:0.5px solid rgba(0,245,212,0.2); padding:5px 12px; margin-bottom:14px; }
.nextup-cta-text { font-family:'Inter',sans-serif; font-size:14px; line-height:1.65; color:rgba(226,244,240,0.55); margin:0 0 20px; }
.nextup-cta-btn { display:flex; align-items:center; justify-content:center; gap:10px; width:100%; padding:14px 24px; background:rgba(0,245,212,0.1); color:#00f5d4; font-family:'JetBrains Mono',monospace; font-size:13px; font-weight:500; text-decoration:none; letter-spacing:0.06em; border:0.5px solid rgba(0,245,212,0.3); transition:background 0.2s,transform 0.2s; }
.nextup-cta-btn:hover { background:rgba(0,245,212,0.18); transform:translateY(-2px); }


.code-forge-section { position:relative; background:#120a06; padding:80px 24px; overflow:hidden; border-top:1px solid rgba(255,122,69,0.1); border-bottom:1px solid rgba(255,122,69,0.1); }
.cf-bg-grid { position:absolute; inset:0; background-image:linear-gradient(rgba(255,122,69,0.03) 1px,transparent 1px),linear-gradient(90deg,rgba(255,122,69,0.03) 1px,transparent 1px); background-size:52px 52px; pointer-events:none; }
.cf-glow { position:absolute; top:-200px; right:-200px; width:700px; height:700px; background:radial-gradient(circle,rgba(255,122,69,0.07) 0%,transparent 70%); pointer-events:none; }
.cf-inner { position:relative; z-index:2; max-width:1200px; margin:0 auto; display:grid; grid-template-columns:1fr 1fr; gap:80px; align-items:start; }
.cf-eyebrow { font-family:'JetBrains Mono',monospace; font-size:10px; font-weight:500; letter-spacing:0.2em; text-transform:uppercase; color:rgba(255,122,69,0.75); margin-bottom:16px; display:flex; align-items:center; gap:12px; }
.cf-eyebrow::before { content:''; display:block; width:32px; height:1px; background:#ff7a45; }
.cf-logo-row { display:flex; align-items:center; gap:16px; margin-bottom:20px; }
.cf-mark { width:56px; height:56px; background:rgba(255,122,69,0.1); border:1px solid rgba(255,122,69,0.3); display:flex; align-items:center; justify-content:center; font-family:'JetBrains Mono',monospace; font-weight:500; font-size:18px; color:#ff7a45; flex-shrink:0; letter-spacing:0.05em; }
.cf-name-block { display:flex; flex-direction:column; gap:2px; }
.cf-name { font-family:'Space Grotesk',sans-serif; font-size:32px; font-weight:600; color:#f4ece4; margin:0; letter-spacing:-0.02em; line-height:1; }
.cf-name-sub { font-family:'JetBrains Mono',monospace; font-size:11px; color:rgba(244,236,228,0.45); letter-spacing:0.08em; }
.cf-heading { font-family:'Space Grotesk',sans-serif; font-size:28px; font-weight:500; color:#f4ece4; line-height:1.25; margin:0 0 20px; letter-spacing:-0.02em; }
.cf-heading em { color:#ff7a45; font-style:normal; }
.cf-body { font-family:'Inter',sans-serif; font-size:15px; line-height:1.8; color:rgba(244,236,228,0.65); margin:0 0 14px; font-weight:300; }
.cf-body--secondary { color:rgba(244,236,228,0.4); font-size:14px; }
.cf-principles { display:flex; flex-direction:column; margin:28px 0; border:0.5px solid rgba(255,122,69,0.12); }
.cf-principle { display:flex; gap:18px; padding:18px 22px; border-bottom:0.5px solid rgba(255,122,69,0.08); transition:background 0.2s; align-items:flex-start; }
.cf-principle:last-child { border-bottom:none; }
.cf-principle:hover { background:rgba(255,122,69,0.03); }
.cf-principle-num { font-family:'JetBrains Mono',monospace; font-size:13px; color:rgba(255,122,69,0.5); font-weight:600; flex-shrink:0; width:24px; padding-top:2px; }
.cf-principle-title { font-family:'Space Grotesk',sans-serif; font-size:14px; font-weight:500; color:#f4ece4; margin-bottom:5px; }
.cf-principle-text { font-family:'Inter',sans-serif; font-size:12px; line-height:1.65; color:rgba(244,236,228,0.5); font-weight:300; }
.cf-founder-badge { display:inline-flex; align-items:center; gap:10px; background:rgba(255,122,69,0.06); border:0.5px solid rgba(255,122,69,0.28); padding:10px 20px; font-family:'JetBrains Mono',monospace; font-size:11px; font-weight:500; color:rgba(255,122,69,0.85); letter-spacing:0.06em; }
.cf-badge-dot { width:6px; height:6px; border-radius:50%; background:#ff7a45; animation:cfPulse 2s infinite; flex-shrink:0; }
@keyframes cfPulse { 0%,100%{opacity:1;box-shadow:0 0 0 0 rgba(255,122,69,0.4)} 50%{opacity:0.7;box-shadow:0 0 0 5px rgba(255,122,69,0)} }

.cf-stats-card { background:rgba(20,12,7,0.8); border:0.5px solid rgba(255,122,69,0.15); padding:24px 18px; margin-bottom:24px; }
.cf-stats-grid { display:grid; grid-template-columns:repeat(3,1fr); gap:8px; text-align:center; }
.cf-stat-num { font-family:'JetBrains Mono',monospace; font-size:24px; font-weight:600; color:#ff7a45; line-height:1; margin-bottom:8px; }
.cf-stat-label { font-family:'Inter',sans-serif; font-size:10px; font-weight:500; letter-spacing:0.06em; text-transform:uppercase; color:rgba(244,236,228,0.5); line-height:1.4; }

.cf-portfolio-card { background:rgba(20,12,7,0.8); border:0.5px solid rgba(255,122,69,0.12); margin-bottom:24px; }
.cf-portfolio-header { display:flex; align-items:center; gap:8px; padding:14px 18px; border-bottom:0.5px solid rgba(255,122,69,0.08); font-family:'JetBrains Mono',monospace; font-size:10px; letter-spacing:0.12em; color:rgba(255,122,69,0.65); text-transform:uppercase; }
.cf-portfolio-item { display:flex; align-items:center; justify-content:space-between; gap:12px; padding:14px 18px; text-decoration:none; border-bottom:0.5px solid rgba(255,122,69,0.06); transition:background 0.2s; }
.cf-portfolio-item:last-child { border-bottom:none; }
.cf-portfolio-item:hover { background:rgba(255,122,69,0.04); }
.cf-portfolio-item svg { color:rgba(255,122,69,0.4); flex-shrink:0; transition:color 0.2s, transform 0.2s; }
.cf-portfolio-item:hover svg { color:#ff7a45; transform:translate(2px,-2px); }
.cf-portfolio-name { font-family:'Inter',sans-serif; font-size:13px; font-weight:600; color:#f4ece4; margin-bottom:2px; }
.cf-portfolio-category { font-family:'JetBrains Mono',monospace; font-size:10px; color:rgba(244,236,228,0.4); letter-spacing:0.04em; }

.cf-cta-card { background:rgba(255,122,69,0.04); border:0.5px solid rgba(255,122,69,0.2); padding:28px; }
.cf-cta-tag { display:inline-block; font-family:'JetBrains Mono',monospace; font-size:10px; font-weight:500; letter-spacing:0.12em; text-transform:uppercase; color:rgba(255,122,69,0.75); background:rgba(255,122,69,0.08); border:0.5px solid rgba(255,122,69,0.2); padding:5px 12px; margin-bottom:14px; }
.cf-cta-text { font-family:'Inter',sans-serif; font-size:14px; line-height:1.65; color:rgba(244,236,228,0.55); margin:0 0 20px; }
.cf-cta-btn { display:flex; align-items:center; justify-content:center; gap:10px; width:100%; padding:14px 24px; background:rgba(255,122,69,0.1); color:#ff7a45; font-family:'JetBrains Mono',monospace; font-size:13px; font-weight:500; text-decoration:none; letter-spacing:0.06em; border:0.5px solid rgba(255,122,69,0.32); transition:background 0.2s,transform 0.2s; }
.cf-cta-btn:hover { background:rgba(255,122,69,0.18); transform:translateY(-2px); }

.civil-neighbr-section { position:relative; background:#2c1810; padding:80px 24px; overflow:hidden; }
.cn-bg-shapes { position:absolute; inset:0; pointer-events:none; }
.cn-shape { position:absolute; border-radius:50%; }
.cn-shape-1 { width:700px; height:700px; background:radial-gradient(circle,#8b4513 0%,transparent 70%); top:-300px; left:-200px; opacity:0.45; }
.cn-shape-2 { width:500px; height:500px; background:radial-gradient(circle,#d7a86e 0%,transparent 70%); bottom:-150px; right:-100px; opacity:0.2; }
.cn-grain { position:absolute; inset:0; opacity:0.035; background-image:url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='1'/%3E%3C/svg%3E"); }
.cn-inner { position:relative; z-index:2; max-width:1200px; margin:0 auto; display:grid; grid-template-columns:1fr 1fr; gap:80px; align-items:start; }
.cn-eyebrow { font-family:'Inter',sans-serif; font-size:11px; font-weight:700; letter-spacing:3px; text-transform:uppercase; color:#d7a86e; margin-bottom:16px; }
.cn-logo-row { display:flex; align-items:center; gap:14px; margin-bottom:28px; }
.cn-logo-mark { width:52px; height:52px; background:rgba(215,168,110,0.15); border:1px solid rgba(215,168,110,0.3); border-radius:14px; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
.cn-platform-name { font-family:'Crimson Pro',serif; font-size:42px; font-weight:700; color:#fbeee6; margin:0; letter-spacing:-0.5px; }
.cn-heading { font-family:'Crimson Pro',serif; font-size:28px; font-weight:600; color:rgba(251,238,230,0.9); margin:0 0 20px; font-style:italic; line-height:1.35; }
.cn-body { font-family:'Inter',sans-serif; font-size:16px; line-height:1.75; color:#c4a882; margin:0 0 16px; }
.cn-body--secondary { color:#a1887f; font-size:15px; }
.cn-pillars { display:flex; flex-direction:column; gap:16px; margin:32px 0; padding:28px; background:rgba(255,255,255,0.04); border:1px solid rgba(215,168,110,0.15); border-radius:20px; }
.cn-pillar { display:flex; align-items:center; gap:16px; }
.cn-pillar-icon { font-size:22px; width:40px; text-align:center; flex-shrink:0; }
.cn-pillar-title { font-family:'Inter',sans-serif; font-size:14px; font-weight:700; color:#fbeee6; margin-bottom:2px; }
.cn-pillar-sub { font-family:'Inter',sans-serif; font-size:12px; color:#a1887f; }
.cn-founder-badge { display:inline-flex; align-items:center; gap:10px; background:rgba(215,168,110,0.12); border:1px solid rgba(215,168,110,0.3); border-radius:999px; padding:10px 20px; font-family:'Inter',sans-serif; font-size:13px; font-weight:600; color:#d7a86e; }
.cn-badge-dot { width:8px; height:8px; border-radius:50%; background:#d7a86e; animation:cnPulse 2s infinite; flex-shrink:0; }
@keyframes cnPulse { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:0.5;transform:scale(0.8)} }
.cn-quote-card { background:rgba(255,255,255,0.06); border:1px solid rgba(215,168,110,0.2); border-radius:20px; padding:36px 32px; margin-bottom:24px; }
.cn-quote-mark { font-family:'Crimson Pro',serif; font-size:80px; color:#d7a86e; opacity:0.35; line-height:0.6; margin-bottom:20px; display:block; }
.cn-quote-text { font-family:'Crimson Pro',serif; font-size:22px; font-style:italic; line-height:1.6; color:#fbeee6; margin:0 0 20px; }
.cn-quote-attr { font-family:'Inter',sans-serif; font-size:13px; font-weight:600; color:#a1887f; }
.cn-stats-grid { display:grid; grid-template-columns:repeat(3,1fr); gap:2px; background:rgba(215,168,110,0.15); border-radius:16px; overflow:hidden; margin-bottom:24px; border:1px solid rgba(215,168,110,0.2); }
.cn-stat { background:rgba(44,24,16,0.85); padding:20px 16px; text-align:center; }
.cn-stat-num { font-family:'Crimson Pro',serif; font-size:22px; font-weight:700; color:#fbeee6; margin-bottom:4px; }
.cn-stat-lbl { font-family:'Inter',sans-serif; font-size:11px; color:#a1887f; letter-spacing:0.5px; }
.cn-beta-card { background:rgba(255,255,255,0.05); border:1px solid rgba(215,168,110,0.25); border-radius:20px; padding:28px 28px 24px; }
.cn-beta-tag { display:inline-block; font-family:'Inter',sans-serif; font-size:11px; font-weight:700; letter-spacing:1.5px; text-transform:uppercase; color:#d7a86e; background:rgba(215,168,110,0.15); border:1px solid rgba(215,168,110,0.3); padding:5px 12px; border-radius:999px; margin-bottom:14px; }
.cn-beta-text { font-family:'Inter',sans-serif; font-size:14px; line-height:1.65; color:#c4a882; margin:0 0 20px; }
.cn-cta-btn { display:flex; align-items:center; justify-content:center; gap:10px; width:100%; padding:15px 24px; background:#d7a86e; color:#2c1810; font-family:'Inter',sans-serif; font-size:15px; font-weight:700; text-decoration:none; border-radius:12px; transition:background 0.2s,transform 0.2s; margin-bottom:12px; }
.cn-cta-btn:hover { background:#e8c08a; transform:translateY(-2px); }
.cn-secondary-link { display:block; text-align:center; font-family:'Inter',sans-serif; font-size:13px; color:#a1887f; text-decoration:none; transition:color 0.2s; }
.cn-secondary-link:hover { color:#d7a86e; }

section { padding:64px 24px; }
.section-header { text-align:center; max-width:800px; margin:0 auto 48px; }
.section-label { display:inline-block; font-family:'Inter',sans-serif; font-size:14px; font-weight:600; text-transform:uppercase; letter-spacing:2px; color:#c9a84c; background:rgba(201,168,76,0.1); padding:8px 20px; border-radius:20px; margin-bottom:20px; }
.section-title { font-family:'Crimson Pro',serif; font-size:52px; font-weight:700; color:#f5f0e8; margin-bottom:16px; letter-spacing:-1px; }
.section-description { font-family:'Inter',sans-serif; font-size:20px; line-height:1.6; color:#9ca3af; }

.philosophy-section { background:#0d1117; }
.loading-container { text-align:center; padding:80px 20px; }
.empty-quotes-state { text-align:center; padding:60px 20px; font-family:'Inter',sans-serif; font-size:18px; color:#9ca3af; }
.quotes-carousel { max-width:1000px; margin:0 auto; }
.quote-container { position:relative; min-height:260px; display:flex; align-items:center; justify-content:center; }
.quote-card-large { position:absolute; width:100%; max-width:900px; background:#132038; border-radius:24px; padding:64px 48px; box-shadow:0 10px 40px rgba(0,0,0,0.4); border:1px solid rgba(201,168,76,0.15); }
.quote-icon { color:#8b5cf6; margin-bottom:24px; opacity:0.4; }
.quote-text { font-family:'Crimson Pro',serif; font-size:32px; font-weight:600; line-height:1.5; color:#f5f0e8; margin-bottom:24px; font-style:italic; }
.quote-author { font-family:'Inter',sans-serif; font-size:18px; font-weight:600; color:#c9a84c; }
.carousel-controls { display:flex; justify-content:center; gap:12px; margin-top:40px; }
.carousel-dot { width:12px; height:12px; border-radius:50%; background:#374151; border:none; cursor:pointer; transition:all 0.3s ease; padding:0; }
.carousel-dot.active { background:#c9a84c; width:32px; border-radius:6px; }
.fade-enter-active,.fade-leave-active { transition:opacity 0.8s ease,transform 0.8s ease; }
.fade-enter-from { opacity:0; transform:translateY(20px); }
.fade-leave-to { opacity:0; transform:translateY(-20px); }

.books-section { background:#0d1117; }
.spinner { width:50px; height:50px; margin:0 auto 20px; border:4px solid rgba(255,255,255,0.1); border-top-color:#c9a84c; border-radius:50%; animation:spin 0.8s linear infinite; }
@keyframes spin { to{transform:rotate(360deg)} }
.series-block { max-width:1400px; margin:0 auto 72px; background:#0b1628; border:1px solid rgba(201,168,76,0.2); overflow:hidden; }
.series-block-header { display:flex; align-items:center; justify-content:space-between; padding:28px 40px; border-bottom:1px solid rgba(201,168,76,0.15); background:rgba(201,168,76,0.04); }
.series-block-left { display:flex; align-items:center; gap:18px; }
.series-mark { width:44px; height:44px; background:#c9a84c; color:#0b1628; display:flex; align-items:center; justify-content:center; font-family:'Playfair Display',serif; font-weight:700; font-size:22px; flex-shrink:0; }
.series-eyebrow { font-family:'Inter',sans-serif; font-size:10px; font-weight:700; letter-spacing:0.2em; text-transform:uppercase; color:#8a6e2a; margin-bottom:4px; }
.series-title { font-family:'Playfair Display',serif; font-size:22px; font-weight:700; color:#f5f0e8; margin:0; }
.series-vol-count { text-align:right; border-left:1px solid rgba(201,168,76,0.15); padding-left:32px; }
.series-vol-num { display:block; font-family:'Playfair Display',serif; font-size:36px; font-weight:700; color:#c9a84c; line-height:1; }
.series-vol-label { font-family:'Inter',sans-serif; font-size:10px; letter-spacing:0.12em; text-transform:uppercase; color:#718096; margin-top:4px; display:block; }
.series-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(340px,1fr)); gap:0; }
.series-vol-card { display:flex; gap:28px; padding:36px 40px; border-right:1px solid rgba(201,168,76,0.1); border-bottom:1px solid rgba(201,168,76,0.1); transition:background 0.2s; }
.series-vol-card:hover { background:rgba(201,168,76,0.03); }
.series-cover-wrap { position:relative; flex-shrink:0; }
.series-cover { width:110px; min-height:160px; padding:14px 10px; display:flex; flex-direction:column; align-items:center; text-align:center; gap:5px; border:1px solid rgba(201,168,76,0.4); box-shadow:4px 4px 16px rgba(0,0,0,0.5); transform:rotate(-1.5deg); transition:transform 0.3s; position:relative; }
.series-cover:hover { transform:rotate(0deg) scale(1.04); }
.series-cover::before { content:''; position:absolute; inset:4px; border:1px solid rgba(201,168,76,0.18); pointer-events:none; }
.series-cover.theme-navy { background:linear-gradient(160deg,#0d1f3c 0%,#0b1628 60%,#071020 100%); }
.series-cover.theme-emerald { background:linear-gradient(160deg,#123524 0%,#0e2a1c 60%,#061309 100%); }
.series-cover.theme-bronze { background:linear-gradient(160deg,#3d2e14 0%,#1b1409 60%,#0a0703 100%); }
.series-cover.theme-violet { background:linear-gradient(160deg,#422759 0%,#1f1130 60%,#0d0713 100%); }
.series-cover.theme-steel { background:linear-gradient(160deg,#1B4B59 0%,#0B242C 60%,#030D11 100%); }
.series-cover.theme-crimson { background:linear-gradient(160deg,#4A1420 0%,#2B0C13 60%,#120509 100%); }
.series-cover.theme-teal { background:linear-gradient(160deg,#0F3B3A 0%,#0A2726 60%,#041312 100%); }
.series-cover.theme-amber { background:linear-gradient(160deg,#4A2E0D 0%,#2E1B08 60%,#140C03 100%); }
.series-cover.theme-cyan { background:linear-gradient(160deg,#0D3548 0%,#082330 60%,#03121A 100%); }
.series-cover.theme-olive { background:linear-gradient(160deg,#33380F 0%,#20240A 60%,#0E1004 100%); }
.series-cover.theme-indigo { background:linear-gradient(160deg,#201A45 0%,#14112C 60%,#080614 100%); }
.series-cover.theme-rose { background:linear-gradient(160deg,#431722 0%,#2B0E16 60%,#12060A 100%); }
.series-cover.theme-plum { background:linear-gradient(160deg,#3A1240 0%,#240B29 60%,#0F0512 100%); }
.series-cover.theme-forest { background:linear-gradient(160deg,#2A2410 0%,#1A160A 60%,#0B0904 100%); }
.sc-crown { font-size:16px; filter:sepia(1) hue-rotate(15deg) saturate(3) brightness(1.2); }
.sc-title { font-family:'Playfair Display',serif; font-size:7px; color:#e8cc80; letter-spacing:0.06em; line-height:1.35; }
.sc-title strong { font-size:9.5px; font-weight:900; display:block; letter-spacing:0.02em; margin-top:2px; }
.sc-divider { width:70%; height:1px; background:linear-gradient(90deg,transparent,#c9a84c,transparent); }
.sc-scales { font-size:13px; filter:sepia(1) hue-rotate(15deg) saturate(3) brightness(1.1); }
.sc-series { font-family:'Inter',sans-serif; font-size:5.5px; letter-spacing:0.08em; color:#c8c0b0; line-height:1.4; }
.sc-author { font-family:'Playfair Display',serif; font-size:6px; font-weight:700; color:#e8cc80; letter-spacing:0.1em; }
.sc-heir { font-family:'Inter',sans-serif; font-size:5px; font-style:italic; color:#c8c0b0; }
.series-vol-badge { position:absolute; top:-8px; right:-8px; width:32px; height:32px; background:#c9a84c; border-radius:50%; display:flex; align-items:center; justify-content:center; font-family:'Playfair Display',serif; font-size:8px; font-weight:900; color:#0b1628; transform:rotate(15deg); }
.series-vol-info { flex:1; }
.series-vol-tag { font-family:'Inter',sans-serif; font-size:10px; font-weight:700; letter-spacing:0.15em; text-transform:uppercase; color:#c9a84c; margin-bottom:8px; }
.series-vol-title { font-family:'Playfair Display',serif; font-size:18px; font-weight:700; color:#f5f0e8; margin:0 0 4px; line-height:1.25; }
.series-vol-sub { font-family:'Playfair Display',serif; font-size:12px; font-style:italic; color:#8a6e2a; margin-bottom:10px; line-height:1.45; }
.series-vol-desc { font-family:'Inter',sans-serif; font-size:12px; line-height:1.65; color:#718096; font-weight:300; margin-bottom:16px; }
.series-vol-btn { display:inline-flex; align-items:center; gap:6px; font-family:'Inter',sans-serif; font-size:12px; font-weight:700; letter-spacing:0.06em; color:#0b1628; background:#c9a84c; padding:9px 18px; text-decoration:none; transition:background 0.2s; }
.series-vol-btn:hover { background:#e8cc80; }
.series-vol-btn--soon { background:transparent; border:1px solid rgba(201,168,76,0.35); color:#c8c0b0; cursor:default; }
.series-vol-btn--soon:hover { background:transparent; }
.series-footer { display:flex; align-items:center; justify-content:space-between; padding:20px 40px; border-top:1px solid rgba(201,168,76,0.1); background:rgba(0,0,0,0.15); flex-wrap:wrap; gap:12px; }
.series-footer-label { font-family:'Inter',sans-serif; font-size:12px; color:#718096; font-style:italic; }
.series-footer-link { font-family:'Inter',sans-serif; font-size:12px; font-weight:600; color:#c9a84c; text-decoration:none; letter-spacing:0.04em; }
.series-footer-link:hover { color:#e8cc80; }
.series-mark--covenant { background:#d7a86e; color:#2c1810; }
.series-cover-img { width:110px; min-height:160px; object-fit:cover; border:1px solid rgba(201,168,76,0.4); box-shadow:4px 4px 16px rgba(0,0,0,0.5); transform:rotate(-1.5deg); transition:transform 0.3s; display:block; }
.series-cover-img:hover { transform:rotate(0deg) scale(1.04); }
.covenant-series-block { border-color:rgba(215,168,110,0.3); }
.covenant-series-block .series-block-header { background:rgba(215,168,110,0.05); }
.covenant-series-block .series-vol-tag { color:#d7a86e; }

.other-works-header { display:flex; align-items:center; gap:20px; max-width:1400px; margin:0 auto 48px; }
.other-works-rule { flex:1; height:1px; background:#1f2937; }
.other-works-label { font-family:'Inter',sans-serif; font-size:12px; font-weight:600; text-transform:uppercase; letter-spacing:0.15em; color:#4b5563; white-space:nowrap; }
.books-grid { max-width:1400px; margin:0 auto; display:grid; grid-template-columns:repeat(auto-fit,minmax(300px,1fr)); gap:40px; }
.book-card-modern { background:#132038; border-radius:20px; overflow:hidden; box-shadow:0 4px 20px rgba(0,0,0,0.4); transition:all 0.4s cubic-bezier(0.4,0,0.2,1); animation:fadeInUp 0.6s ease-out both; }
.book-card-modern:hover { transform:translateY(-12px); box-shadow:0 20px 50px rgba(0,0,0,0.5); }
.book-cover-wrapper { position:relative; width:100%; aspect-ratio:2/3; overflow:hidden; background:#1a2744; }
.book-cover-img { width:100%; height:100%; object-fit:cover; transition:transform 0.5s ease; }
.book-card-modern:hover .book-cover-img { transform:scale(1.05); }
.book-overlay { position:absolute; inset:0; background:linear-gradient(to top,rgba(26,26,46,0.95) 0%,transparent 100%); display:flex; align-items:flex-end; justify-content:center; padding:24px; opacity:0; transition:opacity 0.3s ease; }
.book-card-modern:hover .book-overlay { opacity:1; }
.view-book-btn { display:flex; align-items:center; gap:8px; padding:12px 24px; background:#f5f0e8; color:#0d1117; font-family:'Inter',sans-serif; font-size:14px; font-weight:600; text-decoration:none; border-radius:8px; transition:all 0.3s ease; }
.view-book-btn:hover { background:#c9a84c; color:#0b1628; transform:translateY(-2px); }
.book-info { padding:24px; }
.book-title-modern { font-family:'Crimson Pro',serif; font-size:22px; font-weight:700; color:#f5f0e8; line-height:1.3; margin-bottom:8px; }
.book-author-modern { font-family:'Inter',sans-serif; font-size:14px; font-weight:500; color:#c9a84c; margin-bottom:12px; }
.book-description { font-family:'Inter',sans-serif; font-size:15px; line-height:1.6; color:#9ca3af; }

.blog-preview-section { background:#111827; }
.blog-grid { max-width:1400px; margin:0 auto; display:grid; grid-template-columns:repeat(auto-fit,minmax(360px,1fr)); gap:32px; }
.blog-preview-card { background:#1a2744; border-radius:16px; overflow:hidden; border:1px solid rgba(201,168,76,0.1); transition:all 0.4s cubic-bezier(0.4,0,0.2,1); animation:fadeInUp 0.6s ease-out both; }
.blog-preview-card:hover { transform:translateY(-8px); box-shadow:0 20px 40px rgba(0,0,0,0.4); border-color:rgba(201,168,76,0.3); }
.blog-preview-image { position:relative; width:100%; height:220px; overflow:hidden; background:#1a2744; }
.blog-preview-image img { width:100%; height:100%; object-fit:cover; transition:transform 0.5s ease; }
.blog-preview-card:hover .blog-preview-image img { transform:scale(1.08); }
.blog-category-badge { position:absolute; top:16px; left:16px; font-family:'Inter',sans-serif; font-size:12px; font-weight:600; text-transform:uppercase; letter-spacing:0.5px; color:white; background:rgba(139,92,246,0.95); backdrop-filter:blur(10px); padding:6px 14px; border-radius:6px; }
.blog-preview-content { padding:28px; }
.blog-meta { display:flex; align-items:center; gap:16px; margin-bottom:16px; }
.blog-date,.blog-reading-time { display:flex; align-items:center; gap:6px; font-family:'Inter',sans-serif; font-size:13px; font-weight:500; color:#718096; }
.blog-preview-title { font-family:'Crimson Pro',serif; font-size:24px; font-weight:700; color:#f5f0e8; line-height:1.3; margin-bottom:12px; }
.blog-preview-excerpt { font-family:'Inter',sans-serif; font-size:15px; line-height:1.7; color:#9ca3af; margin-bottom:20px; }
.blog-tags { display:flex; flex-wrap:wrap; gap:8px; margin-bottom:20px; }
.blog-tag { font-family:'Inter',sans-serif; font-size:12px; font-weight:500; color:#9ca3af; background:rgba(255,255,255,0.06); padding:5px 12px; border-radius:6px; }
.read-more-link { display:inline-flex; align-items:center; gap:6px; font-family:'Inter',sans-serif; font-size:14px; font-weight:600; color:#c9a84c; text-decoration:none; transition:gap 0.3s ease; }
.blog-preview-card:hover .read-more-link { gap:10px; }
.view-all-container { text-align:center; margin-top:48px; }
.view-all-btn { display:inline-flex; align-items:center; gap:10px; padding:16px 32px; background:#c9a84c; color:#0b1628; font-family:'Inter',sans-serif; font-size:16px; font-weight:600; text-decoration:none; border-radius:12px; transition:all 0.3s ease; box-shadow:0 4px 16px rgba(201,168,76,0.3); }
.view-all-btn:hover { transform:translateY(-2px); box-shadow:0 8px 24px rgba(201,168,76,0.4); gap:14px; }

.mission-section { background:linear-gradient(135deg,#1a1a2e 0%,#16213e 100%); color:white; }
.mission-content { max-width:900px; margin:0 auto; text-align:center; }
.mission-icon { margin:0 auto 32px; color:rgba(255,255,255,0.8); }
.mission-title { font-family:'Crimson Pro',serif; font-size:48px; font-weight:700; margin-bottom:24px; letter-spacing:-1px; }
.mission-text { font-family:'Inter',sans-serif; font-size:20px; line-height:1.8; color:rgba(255,255,255,0.9); margin-bottom:40px; }
.mission-areas { display:flex; flex-wrap:wrap; justify-content:center; gap:12px; }
.area-tag { font-family:'Inter',sans-serif; font-size:14px; font-weight:500; color:white; background:rgba(255,255,255,0.1); backdrop-filter:blur(10px); border:1px solid rgba(255,255,255,0.2); padding:10px 20px; border-radius:8px; text-decoration:none; }
.area-tag--gold { background:rgba(201,168,76,0.15); border-color:rgba(201,168,76,0.5); color:#c9a84c; font-weight:700; transition:background 0.2s; }
.area-tag--gold:hover { background:rgba(201,168,76,0.25); }
.area-tag--teal { background:rgba(0,245,212,0.08); border-color:rgba(0,245,212,0.3); color:#00f5d4; font-weight:700; font-family:'JetBrains Mono',monospace; font-size:12px; letter-spacing:0.04em; transition:background 0.2s; }
.area-tag--teal:hover { background:rgba(0,245,212,0.15); }
.area-tag--highlight { background:rgba(215,168,110,0.2); border-color:rgba(215,168,110,0.5); color:#d7a86e; font-weight:700; transition:background 0.2s; }
.area-tag--highlight:hover { background:rgba(215,168,110,0.3); }
.area-tag--royal { background:rgba(139,92,246,0.15); border-color:rgba(139,92,246,0.4); color:#c4b5fd; font-weight:700; letter-spacing:0.04em; transition:background 0.2s; }
.area-tag--royal:hover { background:rgba(139,92,246,0.25); }

.cta-section { background:#0d1117; padding:80px 24px; }
.cta-content { max-width:900px; margin:0 auto; text-align:center; }
.cta-title { font-family:'Crimson Pro',serif; font-size:52px; font-weight:700; color:#f5f0e8; margin-bottom:16px; letter-spacing:-1px; }
.cta-description { font-family:'Inter',sans-serif; font-size:20px; line-height:1.6; color:#9ca3af; margin-bottom:48px; }
.cta-buttons { display:flex; justify-content:center; gap:16px; flex-wrap:wrap; }
.cta-btn { display:inline-flex; align-items:center; gap:10px; padding:18px 36px; font-family:'Inter',sans-serif; font-size:16px; font-weight:600; text-decoration:none; border-radius:12px; transition:all 0.3s ease; }
.cta-btn.primary { background:#1c2e4a; color:#f5f0e8; border:1px solid rgba(201,168,76,0.2); }
.cta-btn.primary:hover { background:#243d60; transform:translateY(-3px); }
.cta-btn.secondary { background:transparent; color:#c8c0b0; border:1px solid rgba(255,255,255,0.15); }
.cta-btn.secondary:hover { border-color:rgba(255,255,255,0.35); color:#f5f0e8; transform:translateY(-3px); }
.cta-btn.scripture-btn { background:rgba(99,102,241,0.12); color:#a5b4fc; border:1px solid rgba(99,102,241,0.3); }
.cta-btn.scripture-btn:hover { background:rgba(99,102,241,0.22); border-color:rgba(99,102,241,0.5); transform:translateY(-3px); }
.cta-btn.davidic-btn { background:rgba(139,92,246,0.12); color:#c4b5fd; border:1px solid rgba(139,92,246,0.3); }
.cta-btn.davidic-btn:hover { background:rgba(139,92,246,0.22); border-color:rgba(139,92,246,0.5); transform:translateY(-3px); }
.cta-btn.benv-btn { background:#c9a84c; color:#0b1628; border:none; box-shadow:0 4px 16px rgba(201,168,76,0.35); }
.cta-btn.benv-btn:hover { background:#e8cc80; transform:translateY(-3px); box-shadow:0 8px 24px rgba(201,168,76,0.45); }
.cta-btn.nextup-btn { background:rgba(0,245,212,0.08); color:#00f5d4; border:0.5px solid rgba(0,245,212,0.3); font-family:'JetBrains Mono',monospace; font-size:13px; letter-spacing:0.04em; }
.cta-btn.nextup-btn:hover { background:rgba(0,245,212,0.15); transform:translateY(-3px); }
.cta-btn.cn-btn { background:#d7a86e; color:#2c1810; border:none; box-shadow:0 4px 16px rgba(215,168,110,0.35); }
.cta-btn.cn-btn:hover { background:#e8c08a; transform:translateY(-3px); box-shadow:0 8px 24px rgba(215,168,110,0.45); }

@media (max-width:1024px) {
  .benv-inner,.cn-inner,.lineage-inner,.nextup-inner,.cf-inner { grid-template-columns:1fr; gap:48px; }
}
@media (max-width:768px) {
  .hero-title { font-size:42px; }
  .hero-subtitle { font-size:16px; }
  .lineage-text { font-size:11px; letter-spacing:0.12em; }
  .hero-description { font-size:16px; }
  .hero-stats { flex-direction:column; gap:32px; }
  .stat-divider { width:60px; height:1px; }
  .lin-title { font-size:28px; }
  .benv-name { font-size:22px; }
  .benv-heading { font-size:26px; }
  .benv-laws-list { grid-template-columns:1fr; }
  .nextup-name { font-size:24px; }
  .nextup-heading { font-size:22px; }
  .nextup-ledger-grid { grid-template-columns:1fr; }
  .section-title { font-size:36px; }
  .section-description { font-size:17px; }
  .cn-platform-name { font-size:32px; }
  .cn-heading { font-size:22px; }
  .quote-text { font-size:24px; }
  .quote-card-large { padding:40px 28px; }
  .books-grid,.blog-grid { grid-template-columns:1fr; }
  .mission-title { font-size:36px; }
  .mission-text { font-size:18px; }
  .cta-title { font-size:36px; }
  .cta-buttons { flex-direction:column; }
  .cta-btn { width:100%; justify-content:center; }
  .series-block-header { flex-direction:column; align-items:flex-start; gap:20px; padding:24px; }
  .series-vol-count { border-left:none; border-top:1px solid rgba(201,168,76,0.15); padding-left:0; padding-top:16px; text-align:left; }
  .series-grid { grid-template-columns:1fr; }
  .series-footer { flex-direction:column; align-items:flex-start; }
  .psalm-card { padding:28px 20px; }
  .psalm-card__quote { font-size:15px; }
  .davidic-heir-card { flex-direction:column; gap:12px; }
}
</style>